import { useTranslation } from '@client-app/i18n';
import { useAdminContext } from '@/contexts/AdminContext';
import { useBrandPrototype } from '@/contexts/BrandPrototypeContext';
import { useBrandGroup } from '@/contexts/BrandGroupContext';
import {
  getMemberAvatarUrl,
  Permission,
  useAuth,
} from '@/contexts/AuthContext';
import type { WorkspaceRole } from '@/contexts/AuthContext/useWorkspaces';
import { useToast } from '@/contexts/ToastContext';
import Skeleton from '@common/components/Skeleton';
import { Tooltip } from '@common/index';
import {
  featureService,
  type ManageableApplicationResource,
  type WorkspaceDataRole,
} from '@common/api/featureService';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import {
  CaretDown,
  Check,
  CircleNotch,
  MagnifyingGlass,
  Plus,
  Trash,
  User,
  UserCirclePlus,
  UsersThree,
  XCircle,
} from '@phosphor-icons/react';
import {
  getMemberAssignmentRoleOptions,
  isOwnerRoleOption,
} from './roleAccessGuards';
import {
  applyWorkspaceSystemPermissionDetailsToRole,
  buildWorkspaceSystemPermissionGrantInputs,
  mapWorkspaceDataAccessPartsToRoles,
} from './workspaceDataAccessAdapter';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  hasHigherSelfServiceMemberTier,
  isSoloInviteUpgradeRequired,
  resolveEffectiveInviteMemberLimit,
  resolveInvitePlanName,
  resolveInvitePlanCode,
  resolveMemberInviteEligibility,
  shouldShowMemberInviteEntry,
} from './memberInviteAccess';
import {
  getMemberInviteLimitPrompt,
  getMemberInviteUpgradePrompt,
  getMemberRolesUpgradePrompt,
} from './memberInviteUpgradePrompt';
import {
  isProjectAssignmentAccessCeilingError,
  shouldBlockInviteForApplicationResources,
} from './inviteApplicationResourceState';
import Modal from './Modal';
import {
  MemberInviteLimitModal,
  MemberInviteUpgradeModal,
} from './MemberInviteGateModals';
import { formatRoleLabel } from './roleLabels';
import { type Role } from './rolesData';
import RolesTab, { EMPTY_ROLE, RoleDrawer } from './RolesTab';
import { useSharedRoles } from './useSharedRoles';
import PaginationFooter, {
  getVisiblePaginationPages,
} from '@/components/Pagination/PaginationFooter';
import ContactSalesModal from '../../Pricing/ContactSalesModal';
import { resolvePluginWorkspaceState } from '@/utils/pluginWorkspace';
import { getAdminProductFeatures } from '@/utils/adminProductFeatures';
import {
  getCurrentProductCode,
  isPluginProduct,
} from '@common/api/productContext';
import { getIntegrationBrandIcon } from '@client-app/lib/integration-brand-icons';
import {
  type BrandConnectionAccessLevel,
} from './brandPrototypeModel';
import {
  buildInviteManageableApplicationResources,
  buildInviteProjectAccessGrants,
  enrichInviteApplicationResources,
  getInviteProjectAccessDraftKey,
  getProjectConnectionAccessLevels,
  normalizeProjectConnectionAccessLevel,
  type AssignableBrandConnection,
} from './brandAssignableConnections';
import { getManageableBusinessGroups } from './brandProjectActions';
import {
  getBrandIcon,
  getBrandIconImageClassName,
} from './integrations/IntegrationCard';
import ApplicationAccessPermissionControl from './ApplicationAccessPermissionControl';
import { Checkbox } from '@common/components/Checkbox';
import { logAdminWrite, startAdminWrite } from '@/lib/behaviorTracking';
import { formatConnectionMetaLine } from './integrations/connectionMeta';
import {
  getBusinessGroupDisplayName,
} from './brandGroupViewModel';
import { getMemberBusinessGroups } from './memberBusinessGroups';
import MemberBusinessGroupsCell, {
  type MemberBusinessGroupsCellState,
} from './MemberBusinessGroupsCell';

// --- Helper Hook for Toast Adapter ---
const useToastAdapter = () => {
  const { showToast, showActionToast } = useToast();
  return {
    addToast: ({
      title,
      description,
      type,
      duration,
    }: {
      title: string;
      description: string;
      type: 'success' | 'error' | 'info' | 'warning';
      duration?: number;
    }) => {
      if (duration !== undefined) {
        showActionToast({ title, description, type, duration });
        return;
      }
      showToast(`${title}: ${description}`, type);
    },
  };
};

const SYSTEM_WORKSPACE_ROLES: WorkspaceRole[] = ['OWNER', 'ADMIN', 'MEMBER'];
const DATA_ROLES_CACHE_TTL_MS = 15_000;
const MEMBERS_ITEMS_PER_PAGE = 10;
const MEMBER_INVITATION_SUCCESS_TOAST_DURATION_MS = 8000;

const formatIntegerForDisplay = (value: number, locale: string = 'en') => {
  const normalized = Number.isFinite(value) ? Math.trunc(value) : 0;
  return new Intl.NumberFormat(locale).format(Math.max(0, normalized));
};

const normalizeRoleValue = (role: string) => role.trim().toUpperCase();
const isWorkspaceMemberLimitError = (message?: string | null) => {
  const normalizedMessage = String(message || '').toLowerCase();
  return (
    normalizedMessage.includes('includes up to') &&
    normalizedMessage.includes('member')
  );
};

const normalizeIdentityValue = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const getFirstIdentityValue = (...values: unknown[]) => {
  for (const value of values) {
    const normalizedValue = normalizeIdentityValue(value);
    if (normalizedValue) {
      return normalizedValue;
    }
  }
  return '';
};

const isSameIdentity = (left: unknown, right: unknown) => {
  const normalizedLeft = normalizeIdentityValue(left);
  const normalizedRight = normalizeIdentityValue(right);
  return Boolean(
    normalizedLeft && normalizedRight && normalizedLeft === normalizedRight,
  );
};

const isConnectionOwnedByMember = (
  connectionRecord: Record<string, any>,
  member: Record<string, any>,
) => {
  const memberId = getFirstIdentityValue(member.userId, member.id);
  const memberEmail = getFirstIdentityValue(member.email);
  const memberName = getFirstIdentityValue(member.displayName, member.name);

  const ownerId = getFirstIdentityValue(
    connectionRecord.user_id,
    connectionRecord.connected_by_user_id,
    connectionRecord.created_by_user_id,
    connectionRecord.connectedByUserId,
    connectionRecord.createdByUserId,
    connectionRecord.connectedBy,
  );
  const ownerEmail = getFirstIdentityValue(
    connectionRecord.connected_by_email,
    connectionRecord.created_by_email,
    connectionRecord.connectedByEmail,
    connectionRecord.createdByEmail,
    connectionRecord.email,
  );
  const ownerName = getFirstIdentityValue(
    connectionRecord.connected_by_name,
    connectionRecord.created_by_name,
    connectionRecord.connectedByName,
    connectionRecord.createdByName,
  );

  return (
    isSameIdentity(memberId, ownerId) ||
    isSameIdentity(memberEmail, ownerEmail) ||
    isSameIdentity(memberName, ownerName) ||
    isSameIdentity(memberEmail, ownerId)
  );
};

const getMemberOwnedConnectionCount = (
  member: Record<string, any> | null,
  integrations: Array<Record<string, any>>,
) => {
  if (!member) {
    return 0;
  }

  return integrations.reduce((count, integration) => {
    const mappings =
      Array.isArray(integration.mappings) && integration.mappings.length > 0
        ? integration.mappings
        : integration.connected
          ? [integration]
          : [];

    return (
      count +
      mappings.filter((mapping: Record<string, any>) =>
        isConnectionOwnedByMember(
          {
            ...integration,
            ...mapping,
          },
          member,
        ),
      ).length
    );
  }, 0);
};

const isWorkspaceRole = (role: string): role is WorkspaceRole =>
  SYSTEM_WORKSPACE_ROLES.includes(normalizeRoleValue(role) as WorkspaceRole);

type RoleOption = Pick<
  Role,
  'id' | 'name' | 'description' | 'isSystem' | 'roleKey'
>;
type RoleFilterOption = {
  value: string;
  label: string;
};
type DataRolesCacheEntry = {
  workspaceId: string;
  data: WorkspaceDataRole[];
  timestamp: number;
};
type TransferOwnershipTarget = {
  userId: string;
  displayName: string;
  email: string;
  role: string;
};

const mapWorkspaceDataRolesToRoleOptions = (
  workspaceRoles: WorkspaceDataRole[],
): RoleOption[] =>
  workspaceRoles
    .filter((role) => role.status !== 'INACTIVE')
    .map((role) => ({
      id: String(role.id),
      name: role.name,
      description: role.description || '',
      roleKey: role.roleKey,
      isSystem:
        Boolean(role.systemRole) ||
        role.roleType === 'SYSTEM_DEFAULT' ||
        isWorkspaceRole(role.roleKey || role.name),
    }));

const resolveRoleAssignmentValue = (role: RoleOption) => String(role.id);

const getRoleOptionValue = (role: RoleOption) => String(role.id);

const findRoleOptionByValue = (roles: RoleOption[], value: string) =>
  roles.find(
    (role) =>
      getRoleOptionValue(role) === value ||
      normalizeRoleValue(role.name) === normalizeRoleValue(value),
  );

const buildOrderedRoleOptions = (roles: RoleOption[]) =>
  buildPrioritizedRoleOptions(roles, ['owner', 'admin', 'billing', 'member']);

const buildPrioritizedRoleOptions = (
  roles: RoleOption[],
  prioritizedRoleIds: string[],
) => {
  const priorityMap = new Map(
    prioritizedRoleIds.map((roleId, index) => [
      normalizeRoleValue(roleId),
      index,
    ]),
  );
  const prioritizedIdSet = new Set(prioritizedRoleIds);
  const prioritizedRoles = roles
    .filter(
      (role) =>
        prioritizedIdSet.has(role.id) ||
        priorityMap.has(normalizeRoleValue(role.name)),
    )
    .sort(
      (a, b) =>
        (priorityMap.get(normalizeRoleValue(a.id)) ??
          priorityMap.get(normalizeRoleValue(a.name)) ??
          99) -
        (priorityMap.get(normalizeRoleValue(b.id)) ??
          priorityMap.get(normalizeRoleValue(b.name)) ??
          99),
    );
  const remainingRoles = roles
    .filter(
      (role) =>
        !prioritizedIdSet.has(role.id) &&
        !priorityMap.has(normalizeRoleValue(role.name)),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  return [...prioritizedRoles, ...remainingRoles];
};

const formatRoleFilterSummary = (
  selectedValues: string[],
  options: RoleFilterOption[],
) => {
  if (selectedValues.length === 0) {
    return 'ALL_ROLES';
  }

  const selectedSet = new Set(selectedValues.map(normalizeRoleValue));
  const selectedLabels = options
    .filter((option) => selectedSet.has(normalizeRoleValue(option.value)))
    .map((option) => option.label);

  if (selectedLabels.length <= 1) {
    return selectedLabels[0] || 'ALL_ROLES';
  }

  return `${selectedLabels[0]}, +${selectedLabels.length - 1}`;
};

const RoleDropdownContent = ({
  roles,
  selectedValue,
  isLoading,
  onSelect,
  onNewRole,
}: {
  roles: RoleOption[];
  selectedValue: string;
  isLoading: boolean;
  onSelect: (role: RoleOption) => void;
  onNewRole?: () => void;
}) => {
  const { locale, t } = useTranslation();

  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        className="z-[10002] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
        sideOffset={6}
        align="start"
        collisionPadding={8}
        style={{
          width: 'var(--radix-dropdown-menu-trigger-width)',
          minWidth: 220,
          maxWidth: 'calc(100vw - 16px)',
        }}
      >
        <div className="px-3.5 pt-3 pb-1 text-xs font-semibold text-[#8A93B2]">
          {t('website.membersSettings.selectRole')}
        </div>
        <div className="max-h-[280px] overflow-y-auto p-1.5 space-y-0.5">
          {isLoading ? (
            <div className="space-y-0.5">
              <div className="flex items-center rounded-lg px-2 py-2.5">
                <Skeleton width="110px" height="16px" className="rounded" />
              </div>
              <div className="flex items-center rounded-lg px-2 py-2.5">
                <Skeleton width="75px" height="16px" className="rounded" />
              </div>
              <div className="flex items-center rounded-lg px-2 py-2.5">
                <Skeleton width="95px" height="16px" className="rounded" />
              </div>
            </div>
          ) : (
            roles.map((role) => {
              const isSelected =
                getRoleOptionValue(role) === selectedValue ||
                normalizeRoleValue(selectedValue) ===
                  normalizeRoleValue(role.name);

              return (
                <DropdownMenu.Item
                  key={role.id}
                  onSelect={() => onSelect(role)}
                  className={clsx(
                    'flex items-center justify-between gap-3 rounded-lg px-2 py-2.5 text-left text-sm outline-none transition-colors',
                    isSelected ? 'bg-[#1A162F]/5' : 'hover:bg-gray-50',
                  )}
                >
                  <span
                    className={clsx(
                      'font-medium truncate min-w-0',
                      isSelected ? 'text-[#1A162F]' : 'text-text-main',
                    )}
                    title={formatRoleLabel(role.name, t)}
                  >
                    {formatRoleLabel(role.name, t)}
                  </span>
                  {isSelected ? (
                    <Check
                      size={14}
                      weight="regular"
                      className="shrink-0 text-[#1A162F]"
                    />
                  ) : null}
                </DropdownMenu.Item>
              );
            })
          )}
        </div>
        {onNewRole && (
          <>
            <div className="border-t border-[#ECEFF5] mx-1.5" />
            <div className="p-1.5">
              <DropdownMenu.Item
                onSelect={onNewRole}
                className="flex items-center gap-2 rounded-[10px] px-2.5 py-2.5 text-sm font-medium text-[#666F8D] outline-none transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
              >
                <Plus size={14} weight="regular" />
                {t('website.membersSettings.newRole')}
              </DropdownMenu.Item>
            </div>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
};

// --- Components ---

// 表格行骨架屏组件
const MemberRowSkeleton = ({
  showRoleColumn = true,
  showBrandColumn = false,
  showActionsColumn = true,
}: {
  showRoleColumn?: boolean;
  showBrandColumn?: boolean;
  showActionsColumn?: boolean;
} = {}) => (
  <tr>
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <Skeleton width="36px" height="36px" className="rounded-full" />
        <div className="min-w-0 max-w-[200px] flex flex-col gap-2">
          <Skeleton width="120px" height="16px" />
          <Skeleton width="160px" height="14px" />
        </div>
      </div>
    </td>
    {showRoleColumn ? (
      <td className="px-4 py-3">
        <Skeleton width="80px" height="20px" />
      </td>
    ) : null}
    {showBrandColumn ? (
      <td className="px-4 py-3">
        <Skeleton width="120px" height="20px" />
      </td>
    ) : null}
    <td className="px-4 py-3">
      <Skeleton width="70px" height="24px" className="rounded-full" />
    </td>
    <td className="px-4 py-3">
      <Skeleton width="100px" height="16px" />
    </td>
    {showActionsColumn ? (
      <td className="px-4 py-3 text-right">
        <Skeleton width="32px" height="32px" className="inline-block" />
      </td>
    ) : null}
  </tr>
);

const RoleSelect = ({
  member,
  onUpdateRole,
  canChangeRole,
  roles,
  allowCustomRoles,
  isUpdating,
  isLoadingRoles,
  onNewRole,
}: {
  member: {
    role: WorkspaceRole | string;
    userId?: string;
    id?: string;
    isCurrentUser?: boolean;
  };
  onUpdateRole: (role: RoleOption) => void;
  canChangeRole: boolean;
  roles: RoleOption[];
  allowCustomRoles: boolean;
  isUpdating: boolean;
  isLoadingRoles: boolean;
  onNewRole?: () => void;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const displayedRole = normalizeRoleValue(String(member.role || ''));

  const canEdit = (() => {
    if (!canChangeRole) return false;
    if (member.isCurrentUser) return false;
    if (displayedRole === 'OWNER') return false;
    return true;
  })();

  const displayRole = formatRoleLabel(member.role, t);

  if (!canEdit) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-sm font-medium text-text-main max-w-[170px] truncate"
        title={displayRole}
      >
        {displayRole}
      </span>
    );
  }

  const roleOptions = getMemberAssignmentRoleOptions(roles, allowCustomRoles)
    .filter(
      (role, index, allRoles) =>
        allRoles.findIndex((candidate) => candidate.id === role.id) === index,
    )
    .filter((role) => !isOwnerRoleOption(role));
  const orderedRoleOptions = buildPrioritizedRoleOptions(roleOptions, [
    'admin',
    'member',
  ]);

  return (
    <DropdownMenu.Root
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (isUpdating) {
          return;
        }
        setIsOpen(nextOpen);
      }}
    >
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={isUpdating}
          className={clsx(
            'inline-flex items-center justify-start gap-1.5 rounded-lg border border-transparent pl-0 pr-2.5 py-1.5 text-sm font-medium text-text-main transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 max-w-[170px] min-w-0',
            isUpdating && 'cursor-wait opacity-80',
          )}
        >
          <span className="truncate" title={displayRole}>
            {displayRole}
          </span>
          <CaretDown
            size={12}
            weight="regular"
            className={clsx(
              'opacity-50 transition-transform duration-200 shrink-0',
              isOpen && 'rotate-180',
            )}
          />
        </button>
      </DropdownMenu.Trigger>
      <RoleDropdownContent
        roles={orderedRoleOptions}
        selectedValue={member.role}
        isLoading={isLoadingRoles}
        onSelect={(selectedRole) => {
          onUpdateRole(selectedRole);
          setIsOpen(false);
        }}
        onNewRole={
          onNewRole
            ? () => {
                setIsOpen(false);
                onNewRole();
              }
            : undefined
        }
      />
    </DropdownMenu.Root>
  );
};

const MemberActions = ({
  member,
  onRemove,
  canRemove,
  isDisabled,
}: {
  member: {
    role: WorkspaceRole | string;
    isCurrentUser?: boolean;
  };
  onRemove: () => void;
  canRemove: boolean;
  isDisabled?: boolean;
}) => {
  const { t } = useTranslation();
  const displayedRole = normalizeRoleValue(String(member.role || ''));

  if (!canRemove) return null;
  if (member.isCurrentUser) return null;
  if (displayedRole === 'OWNER') return null;

  return (
    <Tooltip content={t('website.membersSettings.removeUser')} side="top">
      <button
        type="button"
        onClick={onRemove}
        disabled={isDisabled}
        aria-label={t('website.membersSettings.removeUser')}
        className={clsx(
          'p-1.5 rounded text-text-sub transition-colors',
          isDisabled
            ? 'cursor-not-allowed opacity-50'
            : 'hover:bg-red-50 hover:text-red-600',
        )}
      >
        <Trash size={20} weight="regular" />
      </button>
    </Tooltip>
  );
};

const PendingInviteActions = ({
  onResend,
  onCancel,
  canInvite,
  isResending,
  isCancelling,
}: {
  onResend: () => void;
  onCancel: () => void;
  canInvite: boolean;
  isResending?: boolean;
  isCancelling?: boolean;
}) => {
  const { t } = useTranslation();
  const resendLabel = t('website.membersSettings.resendInvite');
  const resendingLabel = t('website.membersSettings.resendingInvite');
  const cancelLabel = t('website.membersSettings.cancelInvite');
  const cancellingLabel = t('website.membersSettings.cancellingInvite');
  const resendButtonLabel = isResending ? resendingLabel : resendLabel;
  const cancelButtonLabel = isCancelling ? cancellingLabel : cancelLabel;
  const isBusy = Boolean(isResending || isCancelling);

  if (!canInvite) return null;

  return (
    <div className="inline-flex items-center justify-end gap-2">
      <Tooltip content={resendButtonLabel} side="top">
        <button
          type="button"
          onClick={onResend}
          disabled={isBusy}
          aria-label={resendButtonLabel}
          aria-busy={isResending}
          className={clsx(
            'inline-flex min-w-[84px] items-center justify-center rounded-[8px] border border-[#E8EBF3] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] transition-colors',
            isBusy
              ? 'cursor-not-allowed opacity-60'
              : 'hover:border-[#C8D0E2] hover:bg-[#F8F9FC]',
          )}
        >
          {isResending ? (
            <>
              <span
                aria-hidden="true"
                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#666F8D] border-t-transparent"
              />
              <span className="sr-only">{resendButtonLabel}</span>
            </>
          ) : (
            resendButtonLabel
          )}
        </button>
      </Tooltip>
      <Tooltip content={cancelButtonLabel} side="top">
        <button
          type="button"
          onClick={onCancel}
          disabled={isBusy}
          aria-label={cancelButtonLabel}
          aria-busy={isCancelling}
          className={clsx(
            'inline-flex min-w-[74px] items-center justify-center rounded-[8px] border border-[#F1D3D3] bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors',
            isBusy
              ? 'cursor-not-allowed opacity-60'
              : 'hover:border-red-200 hover:bg-red-50',
          )}
        >
          {isCancelling ? (
            <>
              <span
                aria-hidden="true"
                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-red-500 border-t-transparent"
              />
              <span className="sr-only">{cancelButtonLabel}</span>
            </>
          ) : (
            cancelButtonLabel
          )}
        </button>
      </Tooltip>
    </div>
  );
};

// --- Invite Role Dropdown ---
const InviteRoleDropdown = ({
  value,
  onChange,
  roles,
  disabled,
  onNewRole,
}: {
  value: string;
  onChange: (role: string) => void;
  roles: RoleOption[];
  disabled?: boolean;
  onNewRole?: () => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = findRoleOptionByValue(roles, value) || roles[0];
  const orderedRoles = buildPrioritizedRoleOptions(roles, ['admin', 'member']);

  return (
    <DropdownMenu.Root
      open={disabled ? false : open}
      onOpenChange={disabled ? undefined : setOpen}
    >
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          className={clsx(
            'flex min-w-0 w-full items-center justify-between rounded-[10px] border bg-white px-3.5 py-3 text-left text-sm transition-all disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50',
            open
              ? 'border-[#1A162F] bg-[#F8F9FC] ring-2 ring-[#1A162F]/20'
              : 'border-[#E8EBF3] hover:border-[#C8D0E2]',
          )}
        >
          <div
            className="truncate pr-2 font-normal text-[#1A162F]"
            title={
              selected
                ? formatRoleLabel(selected.name, t)
                : t('website.membersSettings.noRolesAvailable')
            }
          >
            {selected
              ? formatRoleLabel(selected.name, t)
              : t('website.membersSettings.noRolesAvailable')}
          </div>
          <CaretDown
            size={14}
            weight="regular"
            className={clsx(
              'shrink-0 text-[#8A93B2] transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </button>
      </DropdownMenu.Trigger>
      <RoleDropdownContent
        roles={orderedRoles}
        selectedValue={value}
        isLoading={false}
        onSelect={(role) => {
          onChange(getRoleOptionValue(role));
          setOpen(false);
        }}
        onNewRole={
          onNewRole
            ? () => {
                setOpen(false);
                onNewRole();
              }
            : undefined
        }
      />
    </DropdownMenu.Root>
  );
};

const RoleFilterDropdown = ({
  options,
  selectedValues,
  onToggle,
  onClear,
}: {
  options: RoleFilterOption[];
  selectedValues: string[];
  onToggle: (role: string) => void;
  onClear: () => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedSet = new Set(selectedValues.map(normalizeRoleValue));
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const summaryKey = formatRoleFilterSummary(selectedValues, options);
  const summary =
    summaryKey === 'ALL_ROLES'
      ? t('website.membersSettings.allRoles')
      : summaryKey;

  return (
    <div className="flex items-center gap-2 min-w-[220px]">
      <DropdownMenu.Root
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setQuery('');
          }
        }}
      >
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={clsx(
              'inline-flex min-w-[220px] items-center justify-between gap-3 rounded-[10px] border px-3 py-2.5 text-left text-sm transition-all',
              selectedValues.length > 0
                ? 'border-[#C8D0E2] bg-[#F8F9FC] text-[#1A162F]'
                : 'border-[#E8EBF3] bg-white text-[#1A162F] hover:border-[#C8D0E2]',
              open && 'border-[#1A162F] ring-2 ring-[#1A162F]/20',
            )}
          >
            <span className="truncate font-normal">{summary}</span>
            <CaretDown
              size={14}
              weight="regular"
              className={clsx(
                'shrink-0 text-[#8A93B2] transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="z-[10002] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
            sideOffset={8}
            align="start"
            collisionPadding={8}
            style={{
              width: 320,
              maxWidth: 'calc(100vw - 16px)',
            }}
          >
            <div className="border-b border-[#ECEFF5] p-2">
              <div className="relative">
                <MagnifyingGlass
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93B2]"
                />
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => event.stopPropagation()}
                  placeholder={t('website.membersSettings.filterRoles')}
                  className="w-full rounded-[10px] border border-[#E8EBF3] bg-[#FCFCFC] py-2.5 pl-9 pr-3 text-sm text-[#1A162F] focus:outline-none transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:ring-1 focus:ring-[#1A162F]"
                />
              </div>
            </div>
            <div className="max-h-[320px] overflow-y-auto p-1.5">
              {filteredOptions.length > 0 ? (
                <div className="space-y-0.5">
                  {filteredOptions.map((option) => {
                    const isSelected = selectedSet.has(
                      normalizeRoleValue(option.value),
                    );

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onToggle(option.value)}
                        className={clsx(
                          'flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2.5 text-left transition-colors',
                          isSelected
                            ? 'bg-[#F4F5F8] text-[#1A162F]'
                            : 'text-text-main hover:bg-[#F8F9FC]',
                        )}
                      >
                        <span
                          className={clsx(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                            isSelected
                              ? 'border-[#1A162F] bg-[#1A162F] text-white'
                              : 'border-[#C8D0E2] bg-white text-transparent',
                          )}
                        >
                          <Check size={12} weight="bold" />
                        </span>
                        <span className="truncate text-sm font-medium">
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="px-3 py-6 text-center text-sm text-text-sub">
                  {t('website.membersSettings.noRolesFound')}
                </div>
              )}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {selectedValues.length > 0 ? (
        <button
          type="button"
          onClick={onClear}
          className="text-sm font-normal text-[#666F8D] transition-colors hover:text-[#1A162F]"
        >
          {t('website.membersSettings.clear')}
        </button>
      ) : null}
    </div>
  );
};

const InviteBusinessGroupPopover = ({
  availableGroups,
  onAdd,
  onRemove,
  selectedIds,
  disabled,
}: {
  availableGroups: Array<{ id: string; name: string; isDefault?: boolean }>;
  onAdd: (brandId: string) => void;
  onRemove: (brandId: string) => void;
  selectedIds: string[];
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (availableGroups.length === 0) return null;

  return (
    <Popover.Root
      open={disabled ? false : open}
      onOpenChange={disabled ? undefined : setOpen}
    >
      <Popover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          className="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium text-[#666F8D] transition-colors hover:bg-[#F4F5F8] hover:text-[#3D394D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus size={16} weight="bold" />
          {t('website.membersSettings.addBusinessGroup')}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="z-[10050] w-[280px] rounded-xl border border-[#E8EBF3] bg-white p-1.5 shadow-[0_16px_40px_rgba(26,22,47,0.12)]"
        >
          {availableGroups.map((group) => {
            const isSelected = selectedIds.includes(group.id);
            const toggleGroup = () => {
              if (isSelected) {
                onRemove(group.id);
              } else {
                onAdd(group.id);
              }
              setOpen(false);
            };

            return (
              <div
                key={group.id}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={toggleGroup}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleGroup();
                  }
                }}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-[9px] px-3 py-2.5 text-left text-sm outline-none transition-colors hover:bg-[#F4F5F8] focus-visible:bg-[#F4F5F8]"
              >
                <Checkbox
                  checked={isSelected}
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={toggleGroup}
                />
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[#3D394D]">
                    {getBusinessGroupDisplayName(group)}
                  </span>
                  {group.isDefault ? (
                    <span className="shrink-0 rounded-full bg-[#EEF3FF] px-2 py-0.5 text-[11px] text-[#4E6FA8]">
                      {t('website.brandsSettings.simple.defaultBrand')}
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const InviteApplicationAccessRow = ({
  connection,
  selectedProjectIds,
  value,
  onChange,
  disabled,
}: {
  connection: AssignableBrandConnection;
  selectedProjectIds: string[];
  value: BrandConnectionAccessLevel;
  onChange: (level: BrandConnectionAccessLevel) => void;
  disabled?: boolean;
}) => {
  const { locale, t } = useTranslation();
  const application = getIntegrationBrandIcon(connection.provider);
  const accessLabels: Record<BrandConnectionAccessLevel, string> = {
    no_access: t('website.membersSettings.noAccess'),
    read: t('website.membersSettings.viewOnly'),
    action: t('website.membersSettings.viewAndTakeAction'),
  };
  const assignedSelectedProjectIds = selectedProjectIds.filter((projectId) =>
    connection.brandIds.includes(projectId),
  );
  const availableAccessLevels = (
    ['no_access', 'read', 'action'] as BrandConnectionAccessLevel[]
  ).filter((accessLevel) =>
    assignedSelectedProjectIds.every((projectId) =>
      getProjectConnectionAccessLevels(connection, projectId).includes(
        accessLevel,
      ),
    ),
  );
  const normalizedValue = assignedSelectedProjectIds.reduce<BrandConnectionAccessLevel>(
    (currentValue, projectId) =>
      normalizeProjectConnectionAccessLevel(
        connection,
        projectId,
        undefined,
        currentValue,
      ),
    value,
  );
  const connectionMeta = formatConnectionMetaLine(t, locale, {
    connectedAt: connection.connectedAt,
    connectedBy:
      connection.connectedByDisplayName || connection.connectedByUserId,
  });

  return (
    <div className="flex flex-col gap-3 border-b border-[#E8EBF3] px-4 py-3.5 last:border-b-0 md:flex-row md:items-center md:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[9px] border border-[#E8EBF3] bg-white">
          <img
            src={application.src || getBrandIcon(application.name)}
            alt=""
            className={getBrandIconImageClassName(application.name, 'h-5 w-5')}
          />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-[#1A162F]">
            {connection.accountName || connection.accountDetail}
          </span>
          <span className="mt-0.5 block truncate text-xs text-[#8A93B2]">
            {application.name}
          </span>
          <span className="mt-0.5 block truncate text-xs text-[#8A93B2]">
            {connectionMeta}
          </span>
        </span>
      </div>
      <ApplicationAccessPermissionControl
        value={normalizedValue}
        onChange={onChange}
        accessLabels={accessLabels}
        availableAccessLevels={availableAccessLevels}
        ariaLabel={`${connection.accountName || connection.accountDetail} ${t('website.membersSettings.applicationAccountAccess')}`}
        canManageAccess={false}
        onCanManageAccessChange={() => undefined}
        manageAccessLabel=""
        manageAccessHelp=""
        showManageAccessControl={false}
        density="compact"
        disabled={disabled}
      />
    </div>
  );
};

// --- Main Component ---

const MembersSettings = () => {
  const { locale, t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'members' | 'roles'>('members');
  const {
    workspaceMembers,
    permissions,
    userBilling,
    currentWorkspace,
    integrations,
    user,
  } = useAuth();
  const { setPendingInvitationAccess } = useBrandPrototype();
  const {
    data: projectData,
    isLoading: isProjectDataLoading,
    error: projectDataError,
    refresh: refreshProjectData,
  } = useBrandGroup();
  const [roles, setRoles] = useSharedRoles(currentWorkspace?.id);
  const { modal } = useAdminContext();
  const {
    members,
    pendingInvites,
    entitlements,
    status: membersStatus,
    pendingOwnerTransfer,
    error,
    refresh,
    inviteMember,
    cancelInvite,
    removeMember,
    transferOwnership,
  } = workspaceMembers;

  // 权限检查（后续会叠加 owner 转交后的本地角色限制）
  const canInviteBase = permissions.checkPermissions([
    Permission.MEMBER_INVITE,
  ]);
  const canRemoveBase = permissions.checkPermissions([
    Permission.MEMBER_REMOVE,
  ]);
  const canChangeRoleBase = permissions.checkPermissions([
    Permission.MEMBER_CHANGE_ROLE,
  ]);
  const workspacePlanCode = resolveInvitePlanCode({
    entitlementPlanCode: entitlements.memberLimit?.planCode,
    currentWorkspacePlanCode: currentWorkspace?.subscription?.plan_code,
    billingSummaryPlanCode: userBilling.summary?.planCode,
    userPlanCode: userBilling.userPlan?.planCode,
  });
  const pluginWorkspace = resolvePluginWorkspaceState({
    currentWorkspacePlanCode: currentWorkspace?.subscription?.plan_code,
    currentWorkspacePlanName: currentWorkspace?.subscription?.plan_name,
    currentWorkspacePlanProvider: currentWorkspace?.subscription?.provider,
    billingSummaryPlanCode: userBilling.summary?.planCode,
    billingSummaryPlanName: userBilling.summary?.planName,
    billingSummaryProvider:
      userBilling.summary?.provider || userBilling.summary?.billingProvider,
    userPlanCode: userBilling.userPlan?.planCode,
    userPlanName: userBilling.userPlan?.planName,
    userPlanProvider: userBilling.userPlan?.provider,
    memberLimit: entitlements.memberLimit,
  });
  const adminProductFeatures = getAdminProductFeatures();
  const canInviteMembers = shouldShowMemberInviteEntry({
    hasInvitePermission: canInviteBase,
    memberInvitesEnabled: adminProductFeatures.memberInvites,
  });
  const isPluginWorkspaceUi = isPluginProduct(getCurrentProductCode());
  const supportsInviteApplicationAccess = !isPluginWorkspaceUi;
  const isSoloWorkspace = isSoloInviteUpgradeRequired(workspacePlanCode);
  const effectiveInviteMemberLimit = resolveEffectiveInviteMemberLimit({
    planCode: workspacePlanCode,
    memberLimit: entitlements.memberLimit,
  });
  const memberInviteEligibility = resolveMemberInviteEligibility({
    hasInvitePermission: canInviteBase,
    memberInvitesEnabled: adminProductFeatures.memberInvites,
    planCode: workspacePlanCode,
    memberCount: members.length,
    memberLimit: effectiveInviteMemberLimit,
  });
  const requiresInviteUpgrade = memberInviteEligibility === 'upgrade-required';
  const invitePlanName = isPluginWorkspaceUi
    ? pluginWorkspace.planName
    : resolveInvitePlanName(workspacePlanCode);
  const inviteLimitPrimaryActionTarget = hasHigherSelfServiceMemberTier(
    workspacePlanCode,
  )
    ? 'plans'
    : 'sales';
  const hasReachedMemberLimit =
    memberInviteEligibility === 'member-limit-reached';

  const { addToast } = useToastAdapter();
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [isRoleOptionsLoading, setIsRoleOptionsLoading] = useState(false);
  const [isRolesTabLoading, setIsRolesTabLoading] = useState(false);
  const rolesTabRequestIdRef = useRef(0);
  const roleOptionsRequestIdRef = useRef(0);
  const dataRolesRequestRef = useRef<Promise<WorkspaceDataRole[]> | null>(null);
  const dataRolesRequestVersionRef = useRef(0);
  const dataRolesCacheRef = useRef<DataRolesCacheEntry | null>(null);
  const inlineRoleDrawerRequestIdRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoleFilters, setSelectedRoleFilters] = useState<string[]>([]);
  const [membersPage, setMembersPage] = useState(1);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('MEMBER');
  const [inviteBusinessGroupIds, setInviteBusinessGroupIds] = useState<
    string[]
  >([]);
  const [inviteConnectionAccessDraft, setInviteConnectionAccessDraft] =
    useState<Record<string, BrandConnectionAccessLevel>>({});
  const [collapsedInviteBusinessGroupIds, setCollapsedInviteBusinessGroupIds] =
    useState<Set<string>>(() => new Set());
  const [
    inviteManageableApplicationResources,
    setInviteManageableApplicationResources,
  ] = useState<ManageableApplicationResource[]>([]);
  const [
    isInviteApplicationResourcesLoading,
    setIsInviteApplicationResourcesLoading,
  ] = useState(false);
  const [inviteApplicationResourceError, setInviteApplicationResourceError] =
    useState<string | null>(null);
  const [
    hasLoadedInviteApplicationResources,
    setHasLoadedInviteApplicationResources,
  ] = useState(false);
  const [
    inviteApplicationResourcesWorkspaceId,
    setInviteApplicationResourcesWorkspaceId,
  ] = useState<string | null>(null);
  const inviteApplicationResourceRequestIdRef = useRef(0);
  const [
    inviteApplicationResourcesReloadKey,
    setInviteApplicationResourcesReloadKey,
  ] = useState(0);
  const [memberToDelete, setMemberToDelete] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] =
    useState(false);
  const [transferTargetMemberId, setTransferTargetMemberId] = useState('');
  const [transferTargetSnapshot, setTransferTargetSnapshot] =
    useState<TransferOwnershipTarget | null>(null);
  const [isTransferringOwnership, setIsTransferringOwnership] = useState(false);
  const [resendingInviteIds, setResendingInviteIds] = useState<Set<string>>(
    new Set(),
  );
  const [cancellingInviteIds, setCancellingInviteIds] = useState<Set<string>>(
    new Set(),
  );
  const [updatingRoleMemberIds, setUpdatingRoleMemberIds] = useState<
    Set<string>
  >(new Set());
  const [optimisticRoleByMemberId, setOptimisticRoleByMemberId] = useState<
    Record<string, string>
  >({});
  const [confirmedDataRoleByMemberId, setConfirmedDataRoleByMemberId] =
    useState<Record<string, string>>({});
  const [emailError, setEmailError] = useState('');
  const [isInviteUpgradeModalOpen, setIsInviteUpgradeModalOpen] =
    useState(false);
  const [isRolesUpgradeModalOpen, setIsRolesUpgradeModalOpen] = useState(false);
  const [isInviteLimitModalOpen, setIsInviteLimitModalOpen] = useState(false);
  const [isInviteLimitContactSalesOpen, setIsInviteLimitContactSalesOpen] =
    useState(false);
  const [inlineEditingRole, setInlineEditingRole] = useState<Role | undefined>(
    undefined,
  );
  const [isInlineRolePreparing, setIsInlineRolePreparing] = useState(false);
  useEffect(() => {
    if (
      !supportsInviteApplicationAccess ||
      !isInviteModalOpen ||
      !canInviteMembers ||
      !currentWorkspace?.id
    ) {
      inviteApplicationResourceRequestIdRef.current += 1;
      setInviteManageableApplicationResources([]);
      setIsInviteApplicationResourcesLoading(false);
      setInviteApplicationResourceError(null);
      setHasLoadedInviteApplicationResources(false);
      setInviteApplicationResourcesWorkspaceId(null);
      return;
    }

    const requestedWorkspaceId = String(currentWorkspace.id);
    const requestId = ++inviteApplicationResourceRequestIdRef.current;
    let active = true;

    setInviteManageableApplicationResources([]);
    setIsInviteApplicationResourcesLoading(true);
    setInviteApplicationResourceError(null);
    setHasLoadedInviteApplicationResources(false);
    setInviteApplicationResourcesWorkspaceId(null);

    void featureService
      .getManageableApplicationResources(requestedWorkspaceId)
      .then((resources) => {
        if (
          !active ||
          requestId !== inviteApplicationResourceRequestIdRef.current
        ) {
          return;
        }
        const normalizedResources = Array.isArray(resources) ? resources : [];
        setInviteManageableApplicationResources(
          normalizedResources,
        );
        setHasLoadedInviteApplicationResources(true);
        setInviteApplicationResourcesWorkspaceId(requestedWorkspaceId);
      })
      .catch((error: unknown) => {
        if (
          !active ||
          requestId !== inviteApplicationResourceRequestIdRef.current
        ) {
          return;
        }
        setInviteManageableApplicationResources([]);
        setHasLoadedInviteApplicationResources(false);
        setInviteApplicationResourcesWorkspaceId(null);
        setInviteApplicationResourceError(
          error instanceof Error
            ? error.message
            : 'Unable to load application access.',
        );
      })
      .finally(() => {
        if (
          active &&
          requestId === inviteApplicationResourceRequestIdRef.current
        ) {
          setIsInviteApplicationResourcesLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    canInviteMembers,
    currentWorkspace?.id,
    inviteApplicationResourcesReloadKey,
    isInviteModalOpen,
    supportsInviteApplicationAccess,
  ]);

  const hasCurrentWorkspaceInviteApplicationResources =
    hasLoadedInviteApplicationResources &&
    inviteApplicationResourcesWorkspaceId ===
      String(currentWorkspace?.id || '');
  const inviteOwnedApplicationConnections = useMemo(
    () =>
      supportsInviteApplicationAccess &&
      canInviteMembers &&
      hasCurrentWorkspaceInviteApplicationResources
        ? enrichInviteApplicationResources(
            buildInviteManageableApplicationResources(
              inviteManageableApplicationResources,
            ),
            {
              projectData,
            },
          )
        : [],
    [
      canInviteMembers,
      hasCurrentWorkspaceInviteApplicationResources,
      inviteManageableApplicationResources,
      projectData,
      supportsInviteApplicationAccess,
    ],
  );
  const inviteApplicationBusinessGroups = useMemo(() => {
    const assignedProjectIds = new Set(
      inviteOwnedApplicationConnections.flatMap((connection) =>
        connection.brandIds,
      ),
    );
    return projectData.brands.filter((businessGroup) =>
      assignedProjectIds.has(businessGroup.id),
    );
  }, [projectData.brands, inviteOwnedApplicationConnections]);
  const inviteApplicationBusinessGroupIds = useMemo(
    () => new Set(inviteApplicationBusinessGroups.map((brand) => brand.id)),
    [inviteApplicationBusinessGroups],
  );
  const defaultInviteBrand = useMemo(
    () =>
      inviteApplicationBusinessGroups.find((brand) => brand.isDefault) ||
      inviteApplicationBusinessGroups[0] ||
      null,
    [inviteApplicationBusinessGroups],
  );
  const hasManageableInviteBusinessGroups = useMemo(
    () =>
      getManageableBusinessGroups(
        projectData.assignmentBrands,
        [user?.id, user?.email].filter(
          (value): value is string => Boolean(value),
        ),
        permissions.isOwner,
      ).length > 0,
    [
      permissions.isOwner,
      projectData.assignmentBrands,
      user?.email,
      user?.id,
    ],
  );
  useEffect(() => {
    if (
      !supportsInviteApplicationAccess ||
      !isInviteModalOpen ||
      !defaultInviteBrand?.id
    ) {
      return;
    }

    setInviteBusinessGroupIds((currentIds) =>
      currentIds.includes(defaultInviteBrand.id)
        ? currentIds
        : [defaultInviteBrand.id, ...currentIds],
    );
    setInviteConnectionAccessDraft((current) => {
      const next = { ...current };
      inviteOwnedApplicationConnections
        .filter((connection) => connection.brandIds.includes(defaultInviteBrand.id))
        .forEach((connection) => {
          const draftKey = getInviteProjectAccessDraftKey(
            defaultInviteBrand.id,
            connection.assignmentKey,
          );
          if (!next[draftKey]) next[draftKey] = 'read';
        });
      return next;
    });
  }, [
    defaultInviteBrand?.id,
    inviteOwnedApplicationConnections,
    isInviteModalOpen,
    supportsInviteApplicationAccess,
  ]);
  const selectedInviteBusinessGroupIds = inviteBusinessGroupIds;
  const selectedInviteBusinessGroups = useMemo(
    () =>
      selectedInviteBusinessGroupIds
        .map((businessGroupId) =>
          inviteApplicationBusinessGroups.find(
            (brand) => brand.id === businessGroupId,
          ),
        )
        .filter(
          (
            businessGroup,
          ): businessGroup is (typeof projectData.brands)[number] =>
            Boolean(businessGroup),
        ),
    [inviteApplicationBusinessGroups, selectedInviteBusinessGroupIds],
  );
  const availableInviteBusinessGroups = useMemo(
    () =>
      inviteApplicationBusinessGroups.filter(
        (businessGroup) =>
          !selectedInviteBusinessGroupIds.includes(businessGroup.id),
      ),
    [inviteApplicationBusinessGroups, selectedInviteBusinessGroupIds],
  );
  const shouldShowInviteApplicationAccess =
    supportsInviteApplicationAccess &&
    (isProjectDataLoading ||
      isInviteApplicationResourcesLoading ||
      Boolean(inviteApplicationResourceError) ||
      hasCurrentWorkspaceInviteApplicationResources ||
      inviteOwnedApplicationConnections.length > 0);
  const shouldBlockInviteForApplicationAccess =
    supportsInviteApplicationAccess &&
    (isProjectDataLoading ||
      shouldBlockInviteForApplicationResources({
        hasLoadedCurrentWorkspaceResources:
          hasCurrentWorkspaceInviteApplicationResources,
        isLoading: isInviteApplicationResourcesLoading,
        loadError: inviteApplicationResourceError,
      }));
  const selectedInvitePermissionCount = useMemo(
    () =>
      selectedInviteBusinessGroupIds.reduce(
        (count, businessGroupId) =>
          count +
          inviteOwnedApplicationConnections.filter(
            (connection) =>
              connection.brandIds.includes(businessGroupId) &&
              (inviteConnectionAccessDraft[
                getInviteProjectAccessDraftKey(
                  businessGroupId,
                  connection.assignmentKey,
                )
              ] || 'read') !== 'no_access',
          ).length,
        0,
      ),
    [
      inviteConnectionAccessDraft,
      inviteOwnedApplicationConnections,
      selectedInviteBusinessGroupIds,
    ],
  );
  const resetInviteDraft = () => {
    setInviteEmail('');
    setEmailError('');
    setInviteRole('MEMBER');
    setInviteBusinessGroupIds([]);
    setInviteConnectionAccessDraft({});
    setCollapsedInviteBusinessGroupIds(new Set());
  };
  const closeInviteModal = () => {
    if (isProcessing) return;
    setIsInviteModalOpen(false);
    resetInviteDraft();
  };
  const inviteUpgradePrompt = getMemberInviteUpgradePrompt(t);
  const ownedConnectionCountForDelete = useMemo(
    () =>
      getMemberOwnedConnectionCount(
        memberToDelete,
        integrations.allIntegrations as Array<Record<string, any>>,
      ),
    [integrations.allIntegrations, memberToDelete],
  );
  const rolesUpgradePrompt = getMemberRolesUpgradePrompt(t);
  const inviteLimitPrompt = getMemberInviteLimitPrompt(t, {
    planName: invitePlanName,
    memberLimit: effectiveInviteMemberLimit,
    primaryActionTarget: inviteLimitPrimaryActionTarget,
  });
  const canManagePluginMembers = !isPluginWorkspaceUi || !isSoloWorkspace;
  const canInvite =
    canInviteMembers &&
    adminProductFeatures.memberInvites &&
    canManagePluginMembers;
  const canRemove =
    canRemoveBase &&
    adminProductFeatures.memberInvites &&
    canManagePluginMembers;
  const canChangeRole =
    canChangeRoleBase &&
    adminProductFeatures.rolesAndPermissions &&
    canManagePluginMembers;
  const canManageMembers = canInvite || canRemove || canChangeRole;
  const canManageRoleAccess =
    canChangeRoleBase &&
    adminProductFeatures.rolesAndPermissions &&
    !isSoloWorkspace;
  const canTransferOwnership =
    adminProductFeatures.rolesAndPermissions &&
    permissions.canTransferOwnership &&
    (!isPluginWorkspaceUi || !isSoloWorkspace);
  const canStartOwnerTransfer = canTransferOwnership && !pendingOwnerTransfer;
  const canLoadMemberRoleOptions =
    adminProductFeatures.rolesAndPermissions &&
    Boolean(currentWorkspace?.id) &&
    !isSoloWorkspace;
  const visibleMemberTabs: Array<'members' | 'roles'> =
    adminProductFeatures.rolesAndPermissions && !isSoloWorkspace
      ? ['members', 'roles']
      : ['members'];
  const showMemberRoleColumn =
    adminProductFeatures.rolesAndPermissions && !isSoloWorkspace;
  const showBusinessGroupColumn = !isPluginWorkspaceUi;
  const inlineEmptyRole = useMemo<Role>(
    () => ({
      ...EMPTY_ROLE,
      permissions: {
        ...EMPTY_ROLE.permissions,
        connections: [],
      },
    }),
    [],
  );

  useEffect(() => {
    rolesTabRequestIdRef.current += 1;
    roleOptionsRequestIdRef.current += 1;
    dataRolesRequestRef.current = null;
    dataRolesRequestVersionRef.current += 1;
    dataRolesCacheRef.current = null;
  }, [currentWorkspace?.id]);

  const fetchWorkspaceDataRoles = useCallback(
    (forceRefresh = false) => {
      if (!currentWorkspace?.id) {
        return Promise.resolve([] as WorkspaceDataRole[]);
      }

      const cachedDataRoles = dataRolesCacheRef.current;
      if (
        !forceRefresh &&
        cachedDataRoles?.workspaceId === currentWorkspace.id &&
        Date.now() - cachedDataRoles.timestamp < DATA_ROLES_CACHE_TTL_MS
      ) {
        return Promise.resolve(cachedDataRoles.data);
      }

      if (forceRefresh || !dataRolesRequestRef.current) {
        const requestVersion = dataRolesRequestVersionRef.current + 1;
        dataRolesRequestVersionRef.current = requestVersion;
        dataRolesRequestRef.current = featureService
          .getWorkspaceDataRoles(currentWorkspace.id)
          .then((dataRoles) => {
            if (dataRolesRequestVersionRef.current === requestVersion) {
              dataRolesCacheRef.current = {
                workspaceId: currentWorkspace.id,
                data: dataRoles,
                timestamp: Date.now(),
              };
            }
            return dataRoles;
          })
          .catch((error) => {
            throw error;
          })
          .finally(() => {
            if (dataRolesRequestVersionRef.current === requestVersion) {
              dataRolesRequestRef.current = null;
            }
          });
      }

      return dataRolesRequestRef.current;
    },
    [currentWorkspace?.id],
  );

  const loadWorkspaceDataRoles = useCallback(
    async (forceRefresh = false, options: { throwOnError?: boolean } = {}) => {
      if (
        !currentWorkspace?.id ||
        isSoloWorkspace ||
        !adminProductFeatures.rolesAndPermissions
      ) {
        setRoles([]);
        setIsRolesTabLoading(false);
        return [];
      }

      const requestId = rolesTabRequestIdRef.current + 1;
      rolesTabRequestIdRef.current = requestId;
      setIsRolesTabLoading(true);
      try {
        const dataRoles = await fetchWorkspaceDataRoles(forceRefresh);
        if (rolesTabRequestIdRef.current !== requestId) {
          return dataRoles;
        }
        setRoles(
          mapWorkspaceDataAccessPartsToRoles(
            {
              workspaceId: currentWorkspace.id,
              roles: dataRoles,
            },
            t,
          ),
        );
        return dataRoles;
      } catch (error) {
        console.error('Failed to load workspace data roles:', error);
        if (options.throwOnError) throw error;
        return [];
      } finally {
        if (rolesTabRequestIdRef.current === requestId) {
          setIsRolesTabLoading(false);
        }
      }
    },
    [
      adminProductFeatures.rolesAndPermissions,
      currentWorkspace?.id,
      fetchWorkspaceDataRoles,
      isSoloWorkspace,
      setRoles,
      t,
    ],
  );

  const loadWorkspaceRoleOptions = useCallback(
    async (forceRefresh = false, options: { throwOnError?: boolean } = {}) => {
      if (
        !currentWorkspace?.id ||
        isSoloWorkspace ||
        !canLoadMemberRoleOptions
      ) {
        setRoleOptions([]);
        setIsRoleOptionsLoading(false);
        return [];
      }

      const requestId = roleOptionsRequestIdRef.current + 1;
      roleOptionsRequestIdRef.current = requestId;
      setIsRoleOptionsLoading(true);
      try {
        const workspaceRoles = await fetchWorkspaceDataRoles(forceRefresh);
        if (roleOptionsRequestIdRef.current !== requestId) {
          return workspaceRoles;
        }
        const nextRoleOptions = mapWorkspaceDataRolesToRoleOptions(
          workspaceRoles || [],
        );
        setRoleOptions(nextRoleOptions);
        return nextRoleOptions;
      } catch (error) {
        console.error('Failed to load workspace role options:', error);
        if (options.throwOnError) throw error;
        return [];
      } finally {
        if (roleOptionsRequestIdRef.current === requestId) {
          setIsRoleOptionsLoading(false);
        }
      }
    },
    [
      canLoadMemberRoleOptions,
      currentWorkspace?.id,
      fetchWorkspaceDataRoles,
      isSoloWorkspace,
    ],
  );

  // 邮箱校验函数
  const validateEmail = (email: string) => {
    if (!email.trim()) {
      return t('website.membersSettings.emailRequired');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('website.membersSettings.emailInvalid');
    }
    return '';
  };

  // 处理邮箱输入变化
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInviteEmail(e.target.value);
    // 输入时清除错误提示
    if (emailError) {
      setEmailError('');
    }
  };

  // Workspace 切换时重置本地 UI 选择态，由 workspaceMembers hook 负责数据加载。
  useEffect(() => {
    setOptimisticRoleByMemberId({});
    setConfirmedDataRoleByMemberId({});
    setUpdatingRoleMemberIds(new Set());
    setIsTransferOwnershipModalOpen(false);
    setTransferTargetMemberId('');
    setTransferTargetSnapshot(null);
    setSelectedRoleFilters([]);
    setMembersPage(1);
  }, [currentWorkspace?.id]);

  useEffect(() => {
    setMembersPage(1);
  }, [currentWorkspace?.id, searchQuery, selectedRoleFilters]);

  useEffect(() => {
    void loadWorkspaceRoleOptions();
  }, [loadWorkspaceRoleOptions]);

  useEffect(() => {
    if (activeTab === 'roles' && adminProductFeatures.rolesAndPermissions) {
      void loadWorkspaceDataRoles();
    }
  }, [
    activeTab,
    adminProductFeatures.rolesAndPermissions,
    loadWorkspaceDataRoles,
  ]);

  useEffect(() => {
    if (
      (!adminProductFeatures.rolesAndPermissions || isSoloWorkspace) &&
      activeTab === 'roles'
    ) {
      setActiveTab('members');
    }
  }, [activeTab, adminProductFeatures.rolesAndPermissions, isSoloWorkspace]);

  const membersWithDataRoles = useMemo(
    () =>
      members.map((member: any) => {
        const memberId = String(member.userId || member.id);
        const confirmedDataRole = confirmedDataRoleByMemberId[memberId];
        const assignedRoleName =
          confirmedDataRole || member.dataRoleName || member.data_role_name;
        const shouldShowDataRole =
          Boolean(assignedRoleName) && member.role !== 'OWNER';

        return {
          ...member,
          role: shouldShowDataRole ? assignedRoleName : member.role,
        };
      }),
    [confirmedDataRoleByMemberId, members],
  );

  const businessGroupsByMemberId = useMemo(
    () =>
      new Map(
        membersWithDataRoles.map((member: any) => {
          const memberId = String(member.userId || member.id);
          return [
            memberId,
            getMemberBusinessGroups({
              memberId,
              memberStatus: member.status,
              businessGroups: projectData.brands,
              memberships: projectData.memberships,
            }),
          ] as const;
        }),
      ),
    [membersWithDataRoles, projectData.brands, projectData.memberships],
  );
  const memberBusinessGroupsCellState: MemberBusinessGroupsCellState =
    isProjectDataLoading
      ? 'loading'
      : projectDataError && projectData.brands.length === 0
        ? 'error'
        : 'ready';
  const retryProjectBusinessGroups = useCallback(() => {
    void refreshProjectData().catch(() => undefined);
  }, [refreshProjectData]);

  const visibleMembers = membersWithDataRoles;
  const visiblePendingInvites = pendingInvites;

  // Filter members and pending invites by search query
  const allMembers = visibleMembers;
  const getMemberRowId = (member: any) => String(member.userId || member.id);
  const resolveMemberRole = (member: any) => {
    const memberId = getMemberRowId(member);
    return optimisticRoleByMemberId[memberId] ?? member.role;
  };
  const roleAssignableMembers = allMembers.map((member: any) => {
    const userId = getMemberRowId(member);

    return {
      userId,
      displayName: member.displayName,
      email: member.email,
      role: String(resolveMemberRole(member) || ''),
      status: String(member.status || ''),
    };
  });
  const inviteRoleOptions = [
    ...roleOptions.filter((role) => !isOwnerRoleOption(role)),
  ];
  const memberAssignmentRoleOptions = useMemo(
    () => [...roleOptions.filter((role) => !isOwnerRoleOption(role))],
    [roleOptions],
  );
  const roleFilterRoleNames = [
    ...allMembers.map((member: any) => String(resolveMemberRole(member) || '')),
    ...visiblePendingInvites.map((invite: any) =>
      String(invite.dataRoleName || invite.data_role_name || invite.role || ''),
    ),
  ].filter(Boolean);
  const roleFilterOptionByName = new Map<string, RoleOption>();
  [...roleOptions, ...roles].forEach((role) => {
    roleFilterOptionByName.set(normalizeRoleValue(role.name), {
      id: role.id,
      name: role.name,
      description: role.description,
      roleKey: role.roleKey,
      isSystem: role.isSystem,
    });
  });
  roleFilterRoleNames.forEach((roleName) => {
    const normalizedRoleName = normalizeRoleValue(roleName);
    if (!roleFilterOptionByName.has(normalizedRoleName)) {
      roleFilterOptionByName.set(normalizedRoleName, {
        id: normalizedRoleName,
        name: roleName,
        description: '',
        roleKey: isWorkspaceRole(roleName) ? normalizedRoleName : undefined,
        isSystem: isWorkspaceRole(roleName),
      });
    }
  });
  const roleFilterRoleOptions = Array.from(roleFilterOptionByName.values());
  const roleFilterOptions = buildOrderedRoleOptions(roleFilterRoleOptions).map(
    (role) => ({
      value: role.name,
      label: formatRoleLabel(role.name, t),
    }),
  );
  const selectedRoleFilterSet = new Set(
    selectedRoleFilters.map(normalizeRoleValue),
  );
  const matchesSelectedRoleFilters = (role?: string | null) =>
    selectedRoleFilterSet.size === 0 ||
    selectedRoleFilterSet.has(normalizeRoleValue(String(role || '')));
  const filteredMembers = allMembers
    .filter(
      (m: any) =>
        (m.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.email?.toLowerCase().includes(searchQuery.toLowerCase())) &&
        matchesSelectedRoleFilters(resolveMemberRole(m)),
    )
    .sort((a: any, b: any) => {
      // 将当前用户放在第一位
      if (a.isCurrentUser && !b.isCurrentUser) return -1;
      if (!a.isCurrentUser && b.isCurrentUser) return 1;
      return 0;
    });

  const filteredPendingInvites = visiblePendingInvites.filter(
    (invite: any) =>
      invite.email.toLowerCase().includes(searchQuery.toLowerCase()) &&
      matchesSelectedRoleFilters(String(invite.role || '')),
  );

  // Combine: members first, then pending invites
  const allRows = [
    ...filteredMembers.map((m: any) => ({
      ...m,
      role: resolveMemberRole(m),
      businessGroups:
        businessGroupsByMemberId.get(getMemberRowId(m)) || [],
      type: 'member' as const,
    })),
    ...filteredPendingInvites.map((invite: any) => ({
      ...invite,
      role: invite.dataRoleName || invite.data_role_name || invite.role,
      workspaceRole: invite.role,
      businessGroups: [],
      type: 'pending' as const,
    })),
  ];
  const totalMemberRows = allRows.length;
  const totalMemberPages = Math.max(
    1,
    Math.ceil(totalMemberRows / MEMBERS_ITEMS_PER_PAGE),
  );
  const visibleMemberPages = useMemo(
    () => getVisiblePaginationPages(membersPage, totalMemberPages),
    [membersPage, totalMemberPages],
  );
  const paginatedRows = allRows.slice(
    (membersPage - 1) * MEMBERS_ITEMS_PER_PAGE,
    membersPage * MEMBERS_ITEMS_PER_PAGE,
  );
  useEffect(() => {
    setMembersPage((page) => Math.min(Math.max(1, page), totalMemberPages));
  }, [totalMemberPages]);
  const shouldShowInitialSkeleton = membersStatus === 'loading';
  const shouldShowLoadError = membersStatus === 'error';
  const rolePriorityMap: Record<string, number> = {
    ADMIN: 0,
    MEMBER: 1,
  };
  const transferCandidates = allMembers
    .filter((member: any) => {
      const userId = getMemberRowId(member);
      const status = String(member.status || '').toUpperCase();
      const role = normalizeRoleValue(String(resolveMemberRole(member) || ''));
      return status === 'ACTIVE' && !member.isCurrentUser && role !== 'OWNER';
    })
    .map(
      (member: any): TransferOwnershipTarget => ({
        userId: getMemberRowId(member),
        displayName: member.displayName,
        email: member.email,
        role: String(resolveMemberRole(member) || ''),
      }),
    )
    .sort((a, b) => {
      const aKey = normalizeRoleValue(a.role);
      const bKey = normalizeRoleValue(b.role);
      const aPriority = rolePriorityMap[aKey] ?? 99;
      const bPriority = rolePriorityMap[bKey] ?? 99;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      return a.displayName.localeCompare(b.displayName);
    });
  const selectedTransferCandidate =
    transferCandidates.find(
      (member) => member.userId === transferTargetMemberId,
    ) || null;
  const selectedTransferTarget =
    selectedTransferCandidate ||
    (isTransferringOwnership &&
    transferTargetSnapshot?.userId === transferTargetMemberId
      ? transferTargetSnapshot
      : null);
  const currentUserMember =
    allMembers.find((member: any) => member.isCurrentUser) || null;
  const canConfirmOwnershipTransfer = Boolean(selectedTransferTarget);

  const toggleRoleFilter = (roleValue: string) => {
    setSelectedRoleFilters((prev) => {
      const normalizedRoleValue = normalizeRoleValue(roleValue);
      const hasRole = prev.some(
        (value) => normalizeRoleValue(value) === normalizedRoleValue,
      );

      if (hasRole) {
        return prev.filter(
          (value) => normalizeRoleValue(value) !== normalizedRoleValue,
        );
      }

      return [...prev, roleValue];
    });
  };

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  const validateInviteDetails = () => {
    const error = validateEmail(inviteEmail);
    if (error) {
      setEmailError(error);
      return false;
    }

    const normalizedInviteEmail = inviteEmail.trim().toLowerCase();
    const isExistingMember = allMembers.some(
      (member: any) =>
        String(member.email || '').toLowerCase() === normalizedInviteEmail,
    );

    if (isExistingMember) {
      setEmailError(t('website.membersSettings.emailAlreadyMember'));
      return false;
    }

    if (hasReachedMemberLimit) {
      setIsInviteModalOpen(false);
      setIsInviteLimitModalOpen(true);
      return false;
    }

    return true;
  };

  const addInviteBusinessGroup = (businessGroupId: string) => {
    if (
      !canInviteMembers ||
      !inviteApplicationBusinessGroupIds.has(businessGroupId)
    ) {
      return;
    }

    setInviteBusinessGroupIds((currentIds) =>
      currentIds.includes(businessGroupId)
        ? currentIds
        : [...currentIds, businessGroupId],
    );
    setCollapsedInviteBusinessGroupIds((currentIds) => {
      const next = new Set(currentIds);
      next.delete(businessGroupId);
      return next;
    });
    setInviteConnectionAccessDraft((current) => {
      const next = { ...current };
      inviteOwnedApplicationConnections
        .filter((connection) => connection.brandIds.includes(businessGroupId))
        .forEach((connection) => {
          const draftKey = getInviteProjectAccessDraftKey(
            businessGroupId,
            connection.assignmentKey,
          );
          if (!next[draftKey]) next[draftKey] = 'read';
        });
      return next;
    });
  };

  const removeInviteBusinessGroup = (businessGroupId: string) => {
    // Every invited workspace user belongs to the default Business group.
    // Keep the UI and submitted access grants aligned with that server rule.
    if (businessGroupId === defaultInviteBrand?.id) {
      return;
    }

    setInviteBusinessGroupIds((currentIds) =>
      currentIds.filter((currentId) => currentId !== businessGroupId),
    );
    setCollapsedInviteBusinessGroupIds((currentIds) => {
      const next = new Set(currentIds);
      next.delete(businessGroupId);
      return next;
    });
  };

  const toggleInviteBusinessGroupCollapse = (businessGroupId: string) => {
    setCollapsedInviteBusinessGroupIds((currentIds) => {
      const next = new Set(currentIds);
      if (next.has(businessGroupId)) {
        next.delete(businessGroupId);
      } else {
        next.add(businessGroupId);
      }
      return next;
    });
  };

  const setAllInviteBusinessGroupAccess = (
    businessGroupId: string,
    accessLevel: BrandConnectionAccessLevel,
  ) => {
    setInviteConnectionAccessDraft((current) => {
      const next = { ...current };
      inviteOwnedApplicationConnections
        .filter((connection) => connection.brandIds.includes(businessGroupId))
        .forEach((connection) => {
          next[
            getInviteProjectAccessDraftKey(
              businessGroupId,
              connection.assignmentKey,
            )
          ] = accessLevel;
        });
      return next;
    });
  };

  const handleInvite = async () => {
    if (!canInvite) {
      logAdminWrite('blocked', { area: 'member', action: 'invite', workspace_id: currentWorkspace?.id, block_reason: 'permission' });
      return;
    }

    if (!validateInviteDetails()) {
      return;
    }

    if (shouldBlockInviteForApplicationAccess) {
      addToast({
        title: t('website.membersSettings.invitationFailed'),
        description: t('website.membersSettings.applicationAccessStillLoading'),
        type: 'error',
      });
      return;
    }
    const invitedEmail = inviteEmail.trim().toLowerCase();
    const projectAccessGrants = supportsInviteApplicationAccess
      ? buildInviteProjectAccessGrants(
          inviteOwnedApplicationConnections,
          selectedInviteBusinessGroupIds,
          inviteConnectionAccessDraft,
        )
      : [];
    setIsProcessing(true);
    let tracking: ReturnType<typeof startAdminWrite> | null = null;
    let mutationSucceeded = false;
    try {
      const selectedInviteRole = findRoleOptionByValue(
        inviteRoleOptions,
        inviteRole,
      );
      if (!selectedInviteRole) {
        throw new Error(
          t('website.membersSettings.selectedRoleNoLongerAvailable'),
        );
      }

      const workspaceRole: WorkspaceRole = 'MEMBER';
      const dataRoleId = Number(selectedInviteRole.id);
      if (!Number.isFinite(dataRoleId)) {
        throw new Error(t('website.membersSettings.selectedAccessRoleInvalid'));
      }

      tracking = startAdminWrite({ area: 'member', action: 'invite', workspace_id: currentWorkspace?.id });
      await inviteMember(
        invitedEmail,
        workspaceRole,
        dataRoleId,
        projectAccessGrants,
        {
          onMutationSucceeded: () => {
            mutationSucceeded = true;
          },
          onPostWriteFailed: (refreshError) => {
            tracking?.postWriteFailed(refreshError, { failure_stage: 'refresh' });
          },
        },
      );
      tracking.succeeded();
      setIsInviteModalOpen(false);
      resetInviteDraft();
      addToast({
        title: t('website.membersSettings.invitationSent'),
        description: t('website.membersSettings.invitationSentDescription', {
          email: invitedEmail,
          workspace: currentWorkspace?.name,
        }),
        type: 'success',
        duration: MEMBER_INVITATION_SUCCESS_TOAST_DURATION_MS,
      });
    } catch (error: any) {
      if (isWorkspaceMemberLimitError(error.message)) {
        tracking?.blocked('member_limit_reached');
        setIsInviteModalOpen(false);
        setIsInviteLimitModalOpen(true);
        return;
      }

      if (mutationSucceeded) {
        tracking?.postWriteFailed(error, { failure_stage: 'refresh' });
      } else {
        tracking?.failed(error);
      }

      if (isProjectAssignmentAccessCeilingError(error)) {
        setInviteApplicationResourcesReloadKey((current) => current + 1);
        addToast({
          title: t('website.membersSettings.invitationFailed'),
          description: t(
            'website.membersSettings.applicationAccessChanged',
          ),
          type: 'error',
        });
        return;
      }

      addToast({
        title: t('website.membersSettings.invitationFailed'),
        description:
          error.message || t('website.membersSettings.failedToInviteUser'),
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const resolvePendingInviteWorkspaceRole = (invite: any): WorkspaceRole => {
    const inviteWorkspaceRole = String(
      invite.workspaceRole || invite.membershipRole || 'MEMBER',
    );
    return isWorkspaceRole(inviteWorkspaceRole)
      ? (normalizeRoleValue(inviteWorkspaceRole) as WorkspaceRole)
      : 'MEMBER';
  };

  const resolvePendingInviteDataRoleId = (invite: any) => {
    const rawDataRoleId = invite.dataRoleId ?? invite.data_role_id;
    if (
      rawDataRoleId !== undefined &&
      rawDataRoleId !== null &&
      rawDataRoleId !== ''
    ) {
      const dataRoleId = Number(rawDataRoleId);
      if (Number.isFinite(dataRoleId)) {
        return dataRoleId;
      }
    }

    const inviteRoleName = String(
      invite.dataRoleName || invite.data_role_name || invite.role || '',
    );
    const matchedRole = inviteRoleOptions.find(
      (role) =>
        normalizeRoleValue(role.name) === normalizeRoleValue(inviteRoleName) ||
        normalizeRoleValue(role.roleKey || '') ===
          normalizeRoleValue(inviteRoleName),
    );
    const matchedDataRoleId = Number(matchedRole?.id);

    return Number.isFinite(matchedDataRoleId) ? matchedDataRoleId : null;
  };

  const handleResendInvite = async (invite: any) => {
    if (!canInvite) return;

    const inviteId = String(invite.id || invite.email || '');
    const email = String(invite.email || '').trim();
    const dataRoleId = resolvePendingInviteDataRoleId(invite);
    if (!email) {
      addToast({
        title: t('website.membersSettings.invitationResendFailed'),
        description: t('website.membersSettings.failedToResendInvite'),
        type: 'error',
      });
      return;
    }

    setResendingInviteIds((prev) => new Set(prev).add(inviteId));
    const tracking = startAdminWrite({ area: 'member', action: 'resend_invite', workspace_id: currentWorkspace?.id, entity_id: invite.id ? String(invite.id) : undefined });
    let mutationSucceeded = false;
    try {
      const workspaceRole = resolvePendingInviteWorkspaceRole(invite);
      await inviteMember(email, workspaceRole, dataRoleId, undefined, {
        onMutationSucceeded: () => {
          mutationSucceeded = true;
        },
        onPostWriteFailed: (refreshError) => {
          tracking.postWriteFailed(refreshError, { failure_stage: 'refresh' });
        },
      });
      tracking.succeeded();
      addToast({
        title: t('website.membersSettings.invitationResent'),
        description: t('website.membersSettings.invitationResentDescription', {
          email,
          workspace: currentWorkspace?.name,
        }),
        type: 'success',
        duration: MEMBER_INVITATION_SUCCESS_TOAST_DURATION_MS,
      });
    } catch (error: any) {
      if (mutationSucceeded) {
        tracking.postWriteFailed(error, { failure_stage: 'refresh' });
      } else {
        tracking.failed(error);
      }
      addToast({
        title: t('website.membersSettings.invitationResendFailed'),
        description:
          error.message || t('website.membersSettings.failedToResendInvite'),
        type: 'error',
      });
    } finally {
      setResendingInviteIds((prev) => {
        const next = new Set(prev);
        next.delete(inviteId);
        return next;
      });
    }
  };

  const handleCancelInvite = async (invite: any) => {
    if (!canInvite) return;

    const inviteId = String(invite.id || invite.email || '');
    const inviteToken = String(invite.token || '').trim();
    const email = String(invite.email || '').trim();

    if (!inviteToken) {
      addToast({
        title: t('website.membersSettings.invitationCancelFailed'),
        description: t('website.membersSettings.failedToCancelInvite'),
        type: 'error',
      });
      return;
    }

    setCancellingInviteIds((prev) => new Set(prev).add(inviteId));
    const tracking = startAdminWrite({ area: 'member', action: 'cancel_invite', workspace_id: currentWorkspace?.id, entity_id: invite.id ? String(invite.id) : undefined });
    let mutationSucceeded = false;
    try {
      await cancelInvite(inviteToken, {
        onMutationSucceeded: () => {
          mutationSucceeded = true;
        },
        onPostWriteFailed: (refreshError) => {
          tracking.postWriteFailed(refreshError, { failure_stage: 'refresh' });
        },
      });
      tracking.succeeded();
      setPendingInvitationAccess(email, []);
      addToast({
        title: t('website.membersSettings.invitationCancelled'),
        description: t(
          'website.membersSettings.invitationCancelledDescription',
          { email },
        ),
        type: 'success',
      });
    } catch (error: any) {
      if (mutationSucceeded) {
        tracking.postWriteFailed(error, { failure_stage: 'refresh' });
      } else {
        tracking.failed(error);
      }
      addToast({
        title: t('website.membersSettings.invitationCancelFailed'),
        description:
          error.message || t('website.membersSettings.failedToCancelInvite'),
        type: 'error',
      });
    } finally {
      setCancellingInviteIds((prev) => {
        const next = new Set(prev);
        next.delete(inviteId);
        return next;
      });
    }
  };

  const handleInviteButtonClick = () => {
    if (requiresInviteUpgrade) {
      logAdminWrite('blocked', {
        area: 'member',
        action: 'invite',
        workspace_id: currentWorkspace?.id,
        block_reason: 'plan_not_included',
      });
      setIsInviteUpgradeModalOpen(true);
      return;
    }

    if (hasReachedMemberLimit) {
      logAdminWrite('blocked', {
        area: 'member',
        action: 'invite',
        workspace_id: currentWorkspace?.id,
        block_reason: 'member_limit_reached',
      });
      setIsInviteLimitModalOpen(true);
      return;
    }

    resetInviteDraft();
    setIsInviteModalOpen(true);
  };

  const handleOpenInviteUpgradePricing = () => {
    setIsInviteUpgradeModalOpen(false);
    modal.openModal('pricing');
  };

  const handleOpenRolesUpgradePricing = () => {
    setIsRolesUpgradeModalOpen(false);
    modal.openModal('pricing');
  };

  const handleInviteLimitPrimaryAction = () => {
    setIsInviteLimitModalOpen(false);
    if (inviteLimitPrompt.primaryActionTarget === 'none') {
      return;
    }
    if (inviteLimitPrompt.primaryActionTarget === 'sales') {
      setIsInviteLimitContactSalesOpen(true);
      return;
    }
    modal.openModal('pricing');
  };

  const closeDeleteMemberModal = () => {
    if (isProcessing) {
      return;
    }

    setMemberToDelete(null);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;

    setIsProcessing(true);
    const memberId = memberToDelete.userId || String(memberToDelete.id);
    const tracking = startAdminWrite({ area: 'member', action: 'remove', workspace_id: currentWorkspace?.id, entity_id: memberId });
    let mutationSucceeded = false;
    try {
      // 使用 userId 作为删除成员的 ID
      await removeMember(memberId, {
        onMutationSucceeded: () => {
          mutationSucceeded = true;
        },
        onPostWriteFailed: (refreshError) => {
          tracking.postWriteFailed(refreshError, { failure_stage: 'refresh' });
        },
      });
      tracking.succeeded();
      void integrations
        .refresh(false, { forceRefresh: true })
        .catch((error) => {
          console.warn(
            '[MembersSettings] Failed to refresh integrations after member removal',
            error,
          );
        });
      setMemberToDelete(null);
      addToast({
        title: t('website.membersSettings.userRemoved'),
        description: t('website.membersSettings.userRemovedDescription'),
        type: 'success',
      });
    } catch (error: any) {
      if (mutationSucceeded) {
        tracking.postWriteFailed(error, { failure_stage: 'refresh' });
      } else {
        tracking.failed(error);
      }
      addToast({
        title: t('website.membersSettings.removalFailed'),
        description:
          error.message || t('website.membersSettings.failedToRemoveUser'),
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const assignRoleToMember = async (
    memberId: string,
    roleOption: RoleOption | string,
    onMutationSucceeded?: () => void,
    onPostWriteFailed?: (error: unknown) => void,
  ): Promise<string> => {
    const roleName =
      typeof roleOption === 'string' ? roleOption : roleOption.name;

    if (!currentWorkspace?.id) {
      throw new Error(t('website.membersSettings.workspaceUnavailable'));
    }

    const dataRole = roleOptions.find(
      (role) =>
        Number.isFinite(Number(role.id)) &&
        (normalizeRoleValue(role.name) === normalizeRoleValue(roleName) ||
          String(role.id) === String(roleName)),
    );
    if (dataRole) {
      const assignment = await featureService.assignWorkspaceDataRole(
        currentWorkspace.id,
        memberId,
        { roleId: Number(dataRole.id) },
      );
      onMutationSucceeded?.();
      const confirmedRoleName = assignment.roleName || dataRole.name;
      setConfirmedDataRoleByMemberId((prev) => ({
        ...prev,
        [memberId]: confirmedRoleName,
      }));
      try {
        await refresh({ throwOnError: true });
        await loadWorkspaceRoleOptions(false, { throwOnError: true });
      } catch (refreshError) {
        onPostWriteFailed?.(refreshError);
      }
      return confirmedRoleName;
    }

    throw new Error(t('website.membersSettings.selectedRoleUnavailable'));
  };

  const handleUpdateRole = async (
    memberId: string,
    role: RoleOption | string,
    previousRole: string,
  ) => {
    const roleName = typeof role === 'string' ? role : role.name;
    const isCurrentUserMember = allMembers.some(
      (member: any) =>
        getMemberRowId(member) === memberId && member.isCurrentUser,
    );

    if (isCurrentUserMember) {
      logAdminWrite('blocked', { area: 'member', action: 'change_role', workspace_id: currentWorkspace?.id, entity_id: memberId, block_reason: 'self_role_change' });
      addToast({
        title: t('website.membersSettings.updateFailed'),
        description: t('website.membersSettings.cannotChangeOwnRole'),
        type: 'error',
      });
      return;
    }

    let didSucceed = false;
    setUpdatingRoleMemberIds((prev) => {
      const next = new Set(prev);
      next.add(memberId);
      return next;
    });
    setOptimisticRoleByMemberId((prev) => ({
      ...prev,
      [memberId]: roleName,
    }));
    const tracking = startAdminWrite({ area: 'member', action: 'change_role', workspace_id: currentWorkspace?.id, entity_id: memberId });
    let mutationSucceeded = false;

    try {
      const updatedRoleName = await assignRoleToMember(
        memberId,
        role,
        () => {
          mutationSucceeded = true;
        },
        (refreshError) => {
          tracking.postWriteFailed(refreshError, { failure_stage: 'refresh' });
        },
      );
      tracking.succeeded();
      didSucceed = true;
      addToast({
        title: t('website.membersSettings.roleUpdated'),
        description: t('website.membersSettings.roleUpdatedDescription', {
          role: updatedRoleName,
        }),
        type: 'success',
      });
    } catch (error: any) {
      if (mutationSucceeded) {
        tracking.postWriteFailed(error, { failure_stage: 'refresh' });
      } else {
        tracking.failed(error);
      }
      setOptimisticRoleByMemberId((prev) => {
        const next = { ...prev };

        if (previousRole) {
          next[memberId] = previousRole;
        } else {
          delete next[memberId];
        }

        return next;
      });
      addToast({
        title: t('website.membersSettings.updateFailed'),
        description:
          error.message ||
          t('website.membersSettings.failedToUpdateMemberRole'),
        type: 'error',
      });
    } finally {
      setUpdatingRoleMemberIds((prev) => {
        const next = new Set(prev);
        next.delete(memberId);
        return next;
      });

      if (didSucceed) {
        setOptimisticRoleByMemberId((prev) => {
          const next = { ...prev };
          delete next[memberId];
          return next;
        });
      }
    }
  };

  const handleBatchReassignMembersRole = async (
    memberIds: string[],
    nextRole: string,
  ) => {
    if (memberIds.length === 0) {
      return;
    }

    setIsProcessing(true);
    const tracking = startAdminWrite({
      area: 'role',
      action: 'reassign_members',
      workspace_id: currentWorkspace?.id,
      entity_count: memberIds.length,
    });
    let succeededCount = 0;
    let firstRefreshError: unknown = null;
    try {
      for (const memberId of memberIds) {
        await assignRoleToMember(
          memberId,
          nextRole,
          () => {
            succeededCount += 1;
          },
          (refreshError) => {
            firstRefreshError ??= refreshError;
          },
        );
      }

      addToast({
        title: t('website.membersSettings.usersReassigned'),
        description: t('website.membersSettings.usersReassignedDescription', {
          count: memberIds.length,
          plural: memberIds.length > 1 ? 's' : '',
          role: formatRoleLabel(nextRole, t),
        }),
        type: 'success',
      });
      if (firstRefreshError) {
        tracking.postWriteFailed(firstRefreshError, {
          failure_stage: 'refresh',
          succeeded_count: succeededCount,
        });
      } else {
        tracking.succeeded({ succeeded_count: succeededCount });
      }
    } catch (error: any) {
      if (succeededCount > 0) {
        tracking.postWriteFailed(error, {
          failure_stage: 'post_write_follow_up',
          succeeded_count: succeededCount,
        });
      } else {
        tracking.failed(error, { succeeded_count: succeededCount });
      }
      addToast({
        title: t('website.membersSettings.reassignmentFailed'),
        description:
          error.message || t('website.membersSettings.failedToUpdateMembers'),
        type: 'error',
      });
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveDataRole = async (role: Role) => {
    if (!currentWorkspace?.id || isSoloWorkspace) {
      throw new Error(t('website.membersSettings.rolePermissionsUnavailable'));
    }

    const systemPermissionGrants =
      buildWorkspaceSystemPermissionGrantInputs(role);
    const tracking = startAdminWrite({ area: 'role', action: Number.isFinite(Number(role.id)) ? 'update' : 'create', workspace_id: currentWorkspace.id, entity_id: role.id });
    let mutationSucceeded = false;
    let savedEntityId = role.id;

    try {
      const roleId = Number(role.id);
      const isExistingRole = Number.isFinite(roleId);
      let savedRole: WorkspaceDataRole;

      if (isExistingRole) {
        savedRole = await featureService.updateWorkspaceDataRole(
          currentWorkspace.id,
          Number(role.id),
          {
            name: role.name.trim(),
            description: role.description,
            grants: systemPermissionGrants,
          },
        );
      } else {
        savedRole = await featureService.createWorkspaceDataRole(
          currentWorkspace.id,
          {
            name: role.name.trim(),
            description: role.description,
            grants: systemPermissionGrants,
          },
        );
      }
      mutationSucceeded = true;
      savedEntityId = String(savedRole.id);
      const savedUiRole: Role = {
        ...role,
        id: String(savedRole.id),
        name: savedRole.name || role.name.trim(),
        description: savedRole.description ?? role.description,
        roleKey: savedRole.roleKey,
        isSystem:
          Boolean(savedRole.systemRole) ||
          savedRole.defaultRole ||
          savedRole.roleType === 'SYSTEM_DEFAULT',
        isEditable:
          (savedRole.roleKey || '').toUpperCase() !== 'OWNER' &&
          (savedRole.defaultRole || savedRole.deletable !== false),
        isDeletable: Boolean(savedRole.deletable),
      };
      tracking.observeRefresh(loadWorkspaceDataRoles(true, { throwOnError: true }), 'role_refresh', { entity_id: savedEntityId });
      tracking.observeRefresh(loadWorkspaceRoleOptions(false, { throwOnError: true }), 'role_refresh', { entity_id: savedEntityId });
      const permissionsRefresh = permissions.refresh({ throwOnError: true });
      tracking.observeRefresh(permissionsRefresh, 'permissions_refresh', { entity_id: savedEntityId });
      // Preserve the original permissions refresh's swallowed-error behavior.
      await permissionsRefresh.catch(() => undefined);
      tracking.succeeded({ entity_id: String(savedRole.id) });

      addToast({
        title: t('website.membersSettings.roleSaved'),
        description: t('website.membersSettings.roleSavedDescription'),
        type: 'success',
      });
      return savedUiRole;
    } catch (error: any) {
      if (mutationSucceeded) {
        tracking.postWriteFailed(error, { failure_stage: 'permissions_refresh', entity_id: savedEntityId });
      } else {
        tracking.failed(error);
      }
      addToast({
        title: t('website.membersSettings.saveFailed'),
        description:
          error.message ||
          t('website.membersSettings.failedToSaveRolePermissions'),
        type: 'error',
      });
      throw error;
    }
  };

  const handleDeleteDataRole = async (role: Role) => {
    if (
      !currentWorkspace?.id ||
      isSoloWorkspace ||
      !Number.isFinite(Number(role.id))
    ) {
      throw new Error(t('website.membersSettings.rolePermissionsUnavailable'));
    }

    const tracking = startAdminWrite({ area: 'role', action: 'delete', workspace_id: currentWorkspace.id, entity_id: role.id });
    let mutationSucceeded = false;
    try {
      await featureService.deleteWorkspaceDataRole(
        currentWorkspace.id,
        Number(role.id),
      );
      mutationSucceeded = true;
      tracking.observeRefresh(loadWorkspaceDataRoles(true, { throwOnError: true }), 'role_refresh');
      tracking.observeRefresh(loadWorkspaceRoleOptions(false, { throwOnError: true }), 'role_refresh');
      addToast({
        title: t('website.membersSettings.roleDeleted'),
        description: t('website.membersSettings.roleDeletedDescription'),
        type: 'success',
      });
      tracking.succeeded();
    } catch (error: any) {
      if (mutationSucceeded) {
        tracking.postWriteFailed(error, { failure_stage: 'role_refresh' });
      } else {
        tracking.failed(error);
      }
      addToast({
        title: t('website.membersSettings.deleteFailed'),
        description:
          error.message || t('website.membersSettings.failedToDeleteRole'),
        type: 'error',
      });
      throw error;
    }
  };

  const handlePrepareDataRole = useCallback(
    async (role: Role) => {
      if (!currentWorkspace?.id || isSoloWorkspace) {
        return role;
      }

      const roleId = Number(role.id);
      const grants = Number.isFinite(roleId)
        ? await featureService.getWorkspaceDataRoleGrants(
            currentWorkspace.id,
            roleId,
          )
        : [];

      return applyWorkspaceSystemPermissionDetailsToRole(role, grants);
    },
    [currentWorkspace?.id, isSoloWorkspace],
  );

  const closeInlineRoleDrawer = () => {
    inlineRoleDrawerRequestIdRef.current += 1;
    setIsInlineRolePreparing(false);
    setInlineEditingRole(undefined);
  };

  const openInlineNewRoleDrawer = canManageRoleAccess
    ? async () => {
        const requestId = inlineRoleDrawerRequestIdRef.current + 1;
        inlineRoleDrawerRequestIdRef.current = requestId;
        setIsInlineRolePreparing(true);
        setInlineEditingRole(inlineEmptyRole);

        try {
          const preparedRole = await handlePrepareDataRole(inlineEmptyRole);
          if (inlineRoleDrawerRequestIdRef.current !== requestId) {
            return;
          }

          setInlineEditingRole((currentRole) =>
            currentRole?.id === inlineEmptyRole.id ? preparedRole : currentRole,
          );
        } catch (error: any) {
          if (inlineRoleDrawerRequestIdRef.current === requestId) {
            setInlineEditingRole(undefined);
            addToast({
              title: t('website.membersSettings.roleAccessUnavailable'),
              description:
                error.message ||
                t('website.membersSettings.failedToLoadNewRoleAccess'),
              type: 'error',
            });
          }
        } finally {
          if (inlineRoleDrawerRequestIdRef.current === requestId) {
            setIsInlineRolePreparing(false);
          }
        }
      }
    : undefined;

  const openTransferOwnershipModal = (targetMemberId: string) => {
    if (!canStartOwnerTransfer) {
      if (pendingOwnerTransfer) {
        addToast({
          title: t('website.workspaceSettings.ownerTransferPendingTitle'),
          description: t(
            'website.workspaceSettings.ownerTransferPendingDescription',
            {
              name:
                pendingOwnerTransfer.targetName ||
                pendingOwnerTransfer.targetEmail ||
                'the selected user',
            },
          ),
          type: 'info',
        });
      }
      return;
    }

    const matchedTarget = transferCandidates.find(
      (member) => member.userId === targetMemberId,
    );
    if (!matchedTarget) {
      return;
    }

    setTransferTargetMemberId(matchedTarget.userId);
    setTransferTargetSnapshot(matchedTarget);
    setIsTransferOwnershipModalOpen(true);
  };

  const handleTransferOwnership = async () => {
    if (!canStartOwnerTransfer) {
      if (pendingOwnerTransfer) {
        addToast({
          title: t('website.workspaceSettings.ownerTransferPendingTitle'),
          description: t(
            'website.workspaceSettings.ownerTransferPendingDescription',
            {
              name:
                pendingOwnerTransfer.targetName ||
                pendingOwnerTransfer.targetEmail ||
                'the selected user',
            },
          ),
          type: 'info',
        });
      }
      return;
    }

    if (!selectedTransferTarget) {
      return;
    }

    const currentUserMemberId = currentUserMember
      ? getMemberRowId(currentUserMember)
      : null;

    if (!currentUserMemberId) {
      addToast({
        title: t('website.workspaceSettings.transferFailedTitle'),
        description: t('website.membersSettings.couldNotResolveCurrentOwner'),
        type: 'error',
      });
      return;
    }

    setIsTransferringOwnership(true);
    const tracking = startAdminWrite({
      area: 'workspace',
      action: 'transfer_ownership',
      workspace_id: currentWorkspace?.id,
      entity_id: selectedTransferTarget.userId,
    });
    let mutationSucceeded = false;
    try {
      await transferOwnership(selectedTransferTarget.userId, 'TRANSFER', {
        onMutationSucceeded: () => {
          mutationSucceeded = true;
        },
        onPostWriteFailed: (refreshError) => {
          tracking.postWriteFailed(refreshError, { failure_stage: 'refresh' });
        },
      });
      try {
        await refresh({ throwOnError: true });
      } catch (refreshError) {
        tracking.postWriteFailed(refreshError, { failure_stage: 'refresh' });
      }
      tracking.succeeded();
      setIsTransferOwnershipModalOpen(false);
      setTransferTargetMemberId('');
      setTransferTargetSnapshot(null);

      addToast({
        title: t('website.workspaceSettings.ownerTransferRequestSentTitle'),
        description: t('website.workspaceSettings.ownerTransferRequestSent', {
          name: selectedTransferTarget.displayName,
        }),
        type: 'success',
      });
    } catch (error: any) {
      if (mutationSucceeded) {
        tracking.postWriteFailed(error, { failure_stage: 'refresh' });
      } else {
        tracking.failed(error);
      }
      addToast({
        title: t('website.workspaceSettings.transferFailedTitle'),
        description:
          error.message ||
          t('website.workspaceSettings.transferFailedFallback'),
        type: 'error',
      });
    } finally {
      setIsTransferringOwnership(false);
    }
  };

  const handleTabChange = (tab: 'members' | 'roles') => {
    if (tab === 'roles' && !adminProductFeatures.rolesAndPermissions) {
      setActiveTab('members');
      return;
    }

    if (tab === 'roles' && isSoloWorkspace) {
      setIsRolesUpgradeModalOpen(true);
      return;
    }

    setActiveTab(tab);
    if (tab === 'roles') {
      void loadWorkspaceDataRoles();
    }
    if (tab === 'members') {
      void loadWorkspaceRoleOptions();
    }
  };

  // 已迁移到 AuthContext utils 中的 getMemberAvatarUrl

  const getStatusBadge = (status: string) => {
    if (status === 'INVITED' || status === 'PENDING') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
          <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
          {t('website.membersSettings.statusPending')}
        </span>
      );
    }
    if (status === 'ACTIVE') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
          {t('website.membersSettings.statusActive')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700">
        {status}
      </span>
    );
  };

  return (
    <div className="flex flex-col space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-[#E8EBF3]">
        {visibleMemberTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={clsx(
              'relative pb-3 px-1 mr-5 text-sm font-medium transition-colors capitalize',
              activeTab === tab
                ? 'text-[#1A162F]'
                : 'text-gray-400 hover:text-gray-700',
            )}
          >
            {tab === 'members'
              ? t('website.membersSettings.accountsTab')
              : t('website.membersSettings.rolesTab')}
            {activeTab === tab && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[#1A162F]" />
            )}
          </button>
        ))}
      </div>

      {adminProductFeatures.rolesAndPermissions && activeTab === 'roles' ? (
        <RolesTab
          roles={roles}
          onRolesChange={setRoles}
          members={roleAssignableMembers}
          onReassignMembersRole={handleBatchReassignMembersRole}
          onSaveRole={handleSaveDataRole}
          onDeleteRole={handleDeleteDataRole}
          onPrepareRole={handlePrepareDataRole}
          canManageRoles={canManageRoleAccess}
          isLoading={isRolesTabLoading}
        />
      ) : (
        <div className="flex flex-col space-y-6">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-1">
            <div className="flex flex-1 flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[260px] max-w-md">
                <MagnifyingGlass
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t('website.membersSettings.searchUsers')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-[10px] border border-[#E8EBF3] bg-white py-2 pl-9 pr-10 text-sm font-normal text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:outline-none focus:border-[#1A162F] focus:ring-1 focus:ring-[#1A162F]"
                />
                {searchQuery.trim() ? (
                  <button
                    type="button"
                    onClick={clearSearch}
                    aria-label={t('website.membersSettings.clearUserSearch')}
                    title={t('website.membersSettings.clearUserSearch')}
                    className="absolute right-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#A0A8C0] transition-colors hover:text-[#1A162F]"
                  >
                    <XCircle size={16} weight="fill" />
                  </button>
                ) : null}
              </div>
              {adminProductFeatures.rolesAndPermissions && !isSoloWorkspace ? (
                <RoleFilterDropdown
                  options={roleFilterOptions}
                  selectedValues={selectedRoleFilters}
                  onToggle={toggleRoleFilter}
                  onClear={() => setSelectedRoleFilters([])}
                />
              ) : null}
            </div>
            {canInviteMembers ? (
              <button
                onClick={handleInviteButtonClick}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors shadow-sm shadow-primary/20"
              >
                <UserCirclePlus size={16} weight="regular" />
                {t('website.membersSettings.addNewUser')}
              </button>
            ) : null}
          </div>

          {/* Members List */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table
                className={clsx(
                  'w-full table-fixed text-left border-collapse',
                  showBusinessGroupColumn
                    ? showMemberRoleColumn
                      ? 'min-w-[980px]'
                      : 'min-w-[900px]'
                    : showMemberRoleColumn
                      ? 'min-w-[760px]'
                      : 'min-w-[680px]',
                )}
              >
                <colgroup>
                  <col
                    className={
                      showBusinessGroupColumn ? 'w-[300px]' : 'w-[35%]'
                    }
                  />
                  {showMemberRoleColumn ? (
                    <col
                      className={
                        showBusinessGroupColumn ? 'w-[180px]' : undefined
                      }
                    />
                  ) : null}
                  {showBusinessGroupColumn ? <col /> : null}
                  <col className="w-[120px]" />
                  <col className="w-[140px]" />
                  {canManageMembers ? <col className="w-[64px]" /> : null}
                </colgroup>
                <thead className="bg-gray-50/50 border-b border-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium text-text-sub">
                      {t('website.membersSettings.user')}
                    </th>
                    {showMemberRoleColumn ? (
                      <th className="px-4 py-3 text-xs font-medium text-text-sub text-left">
                        {t('website.membersSettings.role')}
                      </th>
                    ) : null}
                    {showBusinessGroupColumn ? (
                      <th className="px-4 py-3 text-xs font-medium text-text-sub text-left">
                        {t('website.membersSettings.businessGroups')}
                      </th>
                    ) : null}
                    <th className="whitespace-nowrap px-4 py-3 text-xs font-medium text-text-sub">
                      {t('website.membersSettings.status')}
                    </th>
                    <th className="whitespace-nowrap px-4 py-3 text-xs font-medium text-text-sub">
                      {t('website.membersSettings.joinedDate')}
                    </th>
                    {canManageMembers ? (
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-sub">
                        {t('website.membersSettings.actions')}
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {shouldShowInitialSkeleton ? (
                    <>
                      <MemberRowSkeleton
                        showRoleColumn={showMemberRoleColumn}
                        showBrandColumn={showBusinessGroupColumn}
                        showActionsColumn={canManageMembers}
                      />
                      <MemberRowSkeleton
                        showRoleColumn={showMemberRoleColumn}
                        showBrandColumn={showBusinessGroupColumn}
                        showActionsColumn={canManageMembers}
                      />
                      <MemberRowSkeleton
                        showRoleColumn={showMemberRoleColumn}
                        showBrandColumn={showBusinessGroupColumn}
                        showActionsColumn={canManageMembers}
                      />
                    </>
                  ) : !shouldShowLoadError ? (
                    paginatedRows.map((row) => {
                      const rowMemberId = String(row.userId || row.id);
                      const rowRoleNormalized = normalizeRoleValue(
                        String(row.role || ''),
                      );
                      const isOwnerRow =
                        row.type === 'member' && rowRoleNormalized === 'OWNER';
                      const isRowUpdating =
                        updatingRoleMemberIds.has(rowMemberId) ||
                        isTransferringOwnership;

                      if (isRowUpdating) {
                        return (
                          <MemberRowSkeleton
                            key={row.id}
                            showRoleColumn={showMemberRoleColumn}
                            showBrandColumn={showBusinessGroupColumn}
                            showActionsColumn={canManageMembers}
                          />
                        );
                      }

                      return (
                        <tr
                          key={row.id}
                          className="group hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <img
                                src={getMemberAvatarUrl(row)}
                                alt={
                                  row.type === 'member'
                                    ? row.displayName
                                    : row.email
                                }
                                className="w-9 h-9 rounded-full border border-gray-200 bg-white"
                              />
                              <div className="min-w-0 max-w-[200px]">
                                <div className="font-medium text-text-main truncate">
                                  {row.type === 'member'
                                    ? row.displayName
                                    : row.email}
                                </div>
                                {row.type === 'member' && (
                                  <div className="text-sm text-text-sub truncate">
                                    {row.email}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {showMemberRoleColumn ? (
                            <td className="px-4 py-3">
                              {row.type === 'member' ? (
                                <RoleSelect
                                  member={row}
                                  onUpdateRole={(role) => {
                                    handleUpdateRole(
                                      rowMemberId,
                                      role,
                                      String(row.role || ''),
                                    );
                                  }}
                                  canChangeRole={canChangeRole}
                                  roles={memberAssignmentRoleOptions}
                                  allowCustomRoles={!isSoloWorkspace}
                                  isUpdating={isRowUpdating}
                                  isLoadingRoles={isRoleOptionsLoading}
                                  onNewRole={openInlineNewRoleDrawer}
                                />
                              ) : (
                                <span className="text-sm text-text-main">
                                  {formatRoleLabel(row.role, t)}
                                </span>
                              )}
                            </td>
                          ) : null}
                          {showBusinessGroupColumn ? (
                            <td className="overflow-hidden px-4 py-3">
                              <MemberBusinessGroupsCell
                                businessGroups={row.businessGroups}
                                state={
                                  row.type === 'member'
                                    ? memberBusinessGroupsCellState
                                    : 'ready'
                                }
                                onRetry={
                                  row.type === 'member' &&
                                  memberBusinessGroupsCellState === 'error'
                                    ? retryProjectBusinessGroups
                                    : undefined
                                }
                              />
                            </td>
                          ) : null}
                          <td className="whitespace-nowrap px-4 py-3">
                            {getStatusBadge(row.status)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-text-sub">
                            {row.type === 'member'
                              ? row.joinedAt
                                ? dayjs(row.joinedAt).format('MMM D, YYYY')
                                : '-'
                              : row.createdAt
                                ? dayjs(row.createdAt).format('MMM D, YYYY')
                                : '-'}
                          </td>
                          {canManageMembers ? (
                            <td className="px-4 py-3 text-right">
                              {row.type === 'member' && !isOwnerRow ? (
                                <MemberActions
                                  member={row}
                                  onRemove={() => setMemberToDelete(row)}
                                  canRemove={canRemove}
                                  isDisabled={isRowUpdating}
                                />
                              ) : row.type === 'pending' ? (
                                <PendingInviteActions
                                  onResend={() => handleResendInvite(row)}
                                  onCancel={() => handleCancelInvite(row)}
                                  canInvite={canInvite}
                                  isResending={resendingInviteIds.has(
                                    rowMemberId,
                                  )}
                                  isCancelling={cancellingInviteIds.has(
                                    rowMemberId,
                                  )}
                                />
                              ) : null}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })
                  ) : null}
                </tbody>
              </table>
            </div>

            {shouldShowLoadError ? (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
                <UsersThree
                  size={48}
                  weight="duotone"
                  className="opacity-50 text-text-sub"
                />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-text-main">
                    {t('website.membersSettings.unableToLoadUsers')}
                  </p>
                  <p className="text-sm text-text-sub">
                    {error?.message ||
                      t('website.membersSettings.tryAgainInMoment')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void refresh();
                  }}
                  className="inline-flex items-center rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC]"
                >
                  {t('website.membersSettings.retry')}
                </button>
              </div>
            ) : (
              allRows.length === 0 &&
              membersStatus !== 'loading' && (
                <div className="flex flex-col items-center justify-center py-12 text-text-sub">
                  <User
                    size={48}
                    weight="duotone"
                    className="mb-4 opacity-50"
                  />
                  <p>
                    {t('website.membersSettings.noUsersMatching', {
                      query: searchQuery,
                    })}
                  </p>
                </div>
              )
            )}
            {totalMemberRows > 0 &&
            !shouldShowInitialSkeleton &&
            !shouldShowLoadError ? (
              <PaginationFooter
                ariaLabel={t('website.membersSettings.pagination')}
                currentPage={membersPage}
                getPageLabel={(page) =>
                  t('website.membersSettings.pageNumberLabel', { page })
                }
                hasNextPage={membersPage < totalMemberPages}
                nextPageLabel={t('website.membersSettings.nextPage')}
                onPageChange={setMembersPage}
                pageNumbers={visibleMemberPages}
                previousPageLabel={t('website.membersSettings.previousPage')}
                summary={t('website.membersSettings.showingUsersRange', {
                  start: formatIntegerForDisplay(
                    (membersPage - 1) * MEMBERS_ITEMS_PER_PAGE + 1,
                    locale,
                  ),
                  end: formatIntegerForDisplay(
                    Math.min(
                      membersPage * MEMBERS_ITEMS_PER_PAGE,
                      totalMemberRows,
                    ),
                    locale,
                  ),
                  total: formatIntegerForDisplay(totalMemberRows, locale),
                })}
              />
            ) : null}
          </div>
        </div>
      )}
      {/* end members tab */}

      {/* Plugin invites are workspace-only; Bustly may additionally grant app access. */}
      <Modal
        isOpen={adminProductFeatures.memberInvites && isInviteModalOpen}
        onClose={closeInviteModal}
        title={t('website.membersSettings.inviteUser')}
        width={
          supportsInviteApplicationAccess ? "max-w-4xl" : "max-w-xl"
        }
        bodyClassName="min-h-0 p-0"
        containerClassName="animate-in fade-in zoom-in-95 duration-200"
        contentScrollable={false}
        noPadding
        showHeaderDivider
      >
        <div
          className={clsx(
            "flex min-h-0 flex-col",
            supportsInviteApplicationAccess && "h-[72vh] max-h-[660px]",
          )}
        >
          <div
            className={clsx(
              "min-h-0 flex-1",
              supportsInviteApplicationAccess &&
                "overflow-y-auto [scrollbar-gutter:stable] [overflow-anchor:none]",
            )}
          >
            <div className="space-y-4 px-6 pb-4 pt-4">
              <p className="text-sm leading-6 text-[#666F8D]">
                {t('website.membersSettings.inviteUserDescription')}
              </p>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-main">
                  {t('website.membersSettings.emailAddress')}
                </label>
                <input
                  type="email"
                  placeholder={t(
                    'website.membersSettings.inviteEmailPlaceholder',
                  )}
                  value={inviteEmail}
                  onChange={handleEmailChange}
                  disabled={isProcessing}
                  className={clsx(
                    'w-full rounded-[10px] border bg-white px-3 py-2.5 text-sm font-normal text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:outline-none focus:ring-1 focus:ring-[#1A162F] disabled:cursor-not-allowed disabled:bg-[#F8F9FC] disabled:opacity-50 [&&:-webkit-autofill]:[box-shadow:0_0_0_100px_white_inset] [&&:-webkit-autofill]:[-webkit-text-fill-color:#1A162F]',
                    emailError
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-[#E8EBF3] focus:border-[#1A162F]',
                  )}
                  autoFocus
                />
                {emailError ? (
                  <p className="mt-1 text-sm text-red-600">{emailError}</p>
                ) : null}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-main">
                  {t('website.membersSettings.workspaceRole')}
                </label>
                <InviteRoleDropdown
                  value={inviteRole}
                  onChange={setInviteRole}
                  roles={inviteRoleOptions}
                  disabled={isProcessing}
                  onNewRole={openInlineNewRoleDrawer}
                />
              </div>

              {shouldShowInviteApplicationAccess ? (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text-main">
                    {t('website.membersSettings.initialApplicationAccess')}
                  </label>
                  <div className="space-y-3">
                  {isProjectDataLoading ||
                  isInviteApplicationResourcesLoading ? (
                    <div className="flex min-h-[140px] items-center justify-center rounded-xl border border-dashed border-[#E8EBF3] bg-[#FCFCFC] px-6 text-center">
                      <div className="flex items-center gap-2 text-sm text-[#666F8D]">
                        <CircleNotch size={18} className="animate-spin" />
                        {t('website.membersSettings.loadingProjectsForAccess')}
                      </div>
                    </div>
                  ) : inviteApplicationResourceError ? (
                    <div className="rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] px-6 py-5 text-center">
                      <p className="text-sm font-medium text-[#1A162F]">
                        {t(
                          'website.membersSettings.applicationAccessUnavailable',
                        )}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-[#666F8D]">
                        {t(
                          'website.membersSettings.applicationAccessUnavailableDescription',
                        )}
                      </p>
                    </div>
                  ) : hasCurrentWorkspaceInviteApplicationResources &&
                    inviteOwnedApplicationConnections.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#E8EBF3] bg-[#FCFCFC] px-6 py-8 text-center">
                      <p className="text-sm font-medium text-[#1A162F]">
                        {t(
                          hasManageableInviteBusinessGroups
                            ? 'website.membersSettings.noManageableApplicationResources'
                            : 'website.membersSettings.noOwnedBusinessGroupsForAccess',
                        )}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-[#666F8D]">
                        {t(
                          hasManageableInviteBusinessGroups
                            ? 'website.membersSettings.noManageableApplicationResourcesDescription'
                            : 'website.membersSettings.noOwnedBusinessGroupsForAccessDescription',
                        )}
                      </p>
                    </div>
                  ) : (
                    <>
                      {inviteApplicationBusinessGroups.length > 0 ? (
                        <div className="space-y-3">
                          {selectedInviteBusinessGroups.map((businessGroup) => {
                            const isDefaultBusinessGroup =
                              businessGroup.id === defaultInviteBrand?.id;
                            const businessGroupConnections =
                              inviteOwnedApplicationConnections.filter(
                                (connection) =>
                                  connection.brandIds.includes(
                                    businessGroup.id,
                                  ),
                              );
                            const isCollapsed =
                              collapsedInviteBusinessGroupIds.has(
                                businessGroup.id,
                              );
                            const groupAccessLevels =
                              businessGroupConnections.map(
                                (connection) =>
                                  inviteConnectionAccessDraft[
                                    getInviteProjectAccessDraftKey(
                                      businessGroup.id,
                                      connection.assignmentKey,
                                    )
                                  ] || 'read',
                              );
                            const batchAccessValue =
                              groupAccessLevels.length > 0 &&
                              groupAccessLevels.every(
                                (accessLevel) =>
                                  accessLevel === groupAccessLevels[0],
                              )
                                ? groupAccessLevels[0]
                                : undefined;
                            const availableBatchAccessLevels = (
                              [
                                'no_access',
                                'read',
                                'action',
                              ] as BrandConnectionAccessLevel[]
                            ).filter((accessLevel) =>
                              businessGroupConnections.every((connection) =>
                                getProjectConnectionAccessLevels(
                                  connection,
                                  businessGroup.id,
                                ).includes(accessLevel),
                              ),
                            );

                            return (
                              <section
                                key={businessGroup.id}
                                className="overflow-hidden rounded-xl border border-[#E8EBF3] bg-white"
                              >
                                <div
                                  role="button"
                                  tabIndex={0}
                                  aria-expanded={!isCollapsed}
                                  onClick={() =>
                                    toggleInviteBusinessGroupCollapse(
                                      businessGroup.id,
                                    )
                                  }
                                  onKeyDown={(event) => {
                                    if (event.target !== event.currentTarget) {
                                      return;
                                    }
                                    if (
                                      event.key === 'Enter' ||
                                      event.key === ' '
                                    ) {
                                      event.preventDefault();
                                      toggleInviteBusinessGroupCollapse(
                                        businessGroup.id,
                                      );
                                    }
                                  }}
                                  className="flex cursor-pointer select-none flex-wrap items-center justify-between gap-2 bg-[#FCFCFC] px-4 py-3 outline-none transition-colors hover:bg-[#F4F5F8] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1A162F]/20"
                                >
                                  <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                                    <span className="font-medium text-[#1A162F]">
                                      {getBusinessGroupDisplayName(businessGroup)}
                                    </span>
                                  </div>
                                  <div
                                    className="flex items-center gap-1"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    {!isDefaultBusinessGroup ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeInviteBusinessGroup(
                                            businessGroup.id,
                                          )
                                        }
                                        disabled={isProcessing}
                                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[#666F8D] transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        <XCircle size={14} weight="regular" />
                                        {t(
                                          'website.membersSettings.removeBusinessGroup',
                                        )}
                                      </button>
                                    ) : null}
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#8A93B2]">
                                      <CaretDown
                                        size={14}
                                        weight="bold"
                                        className={clsx(
                                          'transition-transform duration-200 ease-in-out',
                                          !isCollapsed && 'rotate-180',
                                        )}
                                      />
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className={clsx(
                                    'grid transition-all duration-200 ease-in-out',
                                    isCollapsed
                                      ? 'grid-rows-[0fr] opacity-0 pointer-events-none'
                                      : 'grid-rows-[1fr] opacity-100',
                                  )}
                                >
                                  <div className="overflow-hidden">
                                    {businessGroupConnections.length > 0 ? (
                                      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[#E8EBF3] bg-[#FCFCFC] px-4 py-2.5">
                                        <span className="mr-auto text-xs font-medium text-[#666F8D]">
                                          {t(
                                            'website.membersSettings.setAllApplicationAccess',
                                          )}
                                        </span>
                                        <ApplicationAccessPermissionControl
                                          value={batchAccessValue}
                                          onChange={(accessLevel) =>
                                            setAllInviteBusinessGroupAccess(
                                              businessGroup.id,
                                              accessLevel,
                                            )
                                          }
                                          accessLabels={{
                                            no_access: t(
                                              'website.membersSettings.noAccess',
                                            ),
                                            read: t(
                                              'website.membersSettings.viewOnly',
                                            ),
                                            action: t(
                                              'website.membersSettings.viewAndTakeAction',
                                            ),
                                          }}
                                          availableAccessLevels={
                                            availableBatchAccessLevels
                                          }
                                          ariaLabel={`${getBusinessGroupDisplayName(businessGroup)} ${t(
                                            'website.membersSettings.setAllApplicationAccess',
                                          )}`}
                                          canManageAccess={false}
                                          onCanManageAccessChange={() =>
                                            undefined
                                          }
                                          manageAccessLabel=""
                                          manageAccessHelp=""
                                          showManageAccessControl={false}
                                          density="compact"
                                          disabled={isProcessing}
                                        />
                                      </div>
                                    ) : null}
                                    {businessGroupConnections.length > 0 ? (
                                      <div className="overflow-hidden border-t border-[#E8EBF3] bg-white">
                                        {businessGroupConnections.map(
                                          (connection) => (
                                            <InviteApplicationAccessRow
                                              key={connection.assignmentKey}
                                              connection={connection}
                                              selectedProjectIds={[
                                                businessGroup.id,
                                              ]}
                                              value={
                                                inviteConnectionAccessDraft[
                                                  getInviteProjectAccessDraftKey(
                                                    businessGroup.id,
                                                    connection.assignmentKey,
                                                  )
                                                ] || 'read'
                                              }
                                              onChange={(accessLevel) =>
                                                setInviteConnectionAccessDraft(
                                                  (current) => ({
                                                    ...current,
                                                    [getInviteProjectAccessDraftKey(
                                                      businessGroup.id,
                                                      connection.assignmentKey,
                                                    )]: accessLevel,
                                                  }),
                                                )
                                              }
                                              disabled={isProcessing}
                                            />
                                          ),
                                        )}
                                      </div>
                                    ) : (
                                      <div className="border-t border-[#E8EBF3] bg-[#FCFCFC] px-6 py-8 text-center">
                                        <p className="text-sm font-medium text-[#1A162F]">
                                          {t(
                                            'website.membersSettings.noManageableApplicationResources',
                                          )}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </section>
                            );
                          })}
                          <div className="pt-1">
                            <InviteBusinessGroupPopover
                              availableGroups={availableInviteBusinessGroups}
                              onAdd={addInviteBusinessGroup}
                              onRemove={removeInviteBusinessGroup}
                              selectedIds={selectedInviteBusinessGroupIds}
                              disabled={isProcessing}
                            />
                          </div>
                        </div>
                      ) : null}

                    </>
                  )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#E8EBF3] px-6 pb-6 pt-4">
            <button
              type="button"
              onClick={closeInviteModal}
              disabled={isProcessing}
              className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <div className="flex flex-wrap items-center justify-end gap-3">
              {supportsInviteApplicationAccess && selectedInvitePermissionCount > 0 ? (
                <span className="text-xs text-[#8A93B2]">
                  {t('website.membersSettings.applicationAccountsSelected', {
                    count: selectedInvitePermissionCount,
                  })}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => void handleInvite()}
                disabled={
                  !inviteEmail.trim() ||
                  isProcessing ||
                  shouldBlockInviteForApplicationAccess
                }
                className="flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E] disabled:cursor-not-allowed disabled:bg-[#D1D5DB] disabled:shadow-none"
              >
                {isProcessing ? (
                  <>
                    <CircleNotch className="h-4 w-4 animate-spin" />
                    {t('website.membersSettings.sendingInvitation')}
                  </>
                ) : (
                  t('website.membersSettings.sendInvitation')
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!memberToDelete}
        onClose={closeDeleteMemberModal}
        title={t('website.membersSettings.removeUser')}
        width="max-w-sm"
        bodyClassName="px-6 pb-6 pt-4"
        showHeaderDivider
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-[#666F8D]">
            {t('website.membersSettings.removeUserConfirmation')}{' '}
            <span className="font-medium text-[#1A162F]">
              {memberToDelete?.displayName}
            </span>
            {t('website.membersSettings.removeUserIrreversible')}
          </p>
          {ownedConnectionCountForDelete > 0 ? (
            <div className="rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-5 text-amber-900">
              {t('website.membersSettings.integrationOwnerRemovalWarning', {
                count: ownedConnectionCountForDelete,
                plural: ownedConnectionCountForDelete === 1 ? '' : 's',
              })}
            </div>
          ) : null}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={closeDeleteMemberModal}
              disabled={isProcessing}
              className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={confirmDelete}
              disabled={isProcessing}
              className="rounded-[10px] bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(220,38,38,0.12)] transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('website.membersSettings.remove')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isTransferOwnershipModalOpen}
        onClose={() => {
          if (isTransferringOwnership) return;
          setIsTransferOwnershipModalOpen(false);
          setTransferTargetMemberId('');
          setTransferTargetSnapshot(null);
        }}
        title={t('website.workspaceSettings.transferOwnership')}
        width="max-w-xl"
        bodyClassName="px-6 pb-6 pt-4"
        showHeaderDivider
      >
        <div className="space-y-4">
          {selectedTransferTarget ? (
            <div className="rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] px-3 py-3">
              <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[#8A93B2]">
                {t('website.membersSettings.newOwner')}
              </p>
              <p className="text-sm font-medium text-[#1A162F]">
                {selectedTransferTarget.displayName}
              </p>
              <p className="mt-1 text-xs text-[#666F8D]">
                {selectedTransferTarget.email}
                {' · '}
                {formatRoleLabel(selectedTransferTarget.role, t)}
              </p>
            </div>
          ) : null}

          <div className="rounded-xl border border-[#E8EBF3] bg-[#F8F9FC] px-3 py-3">
            <p className="text-sm leading-relaxed text-[#666F8D]">
              {selectedTransferTarget?.displayName ||
                t('website.membersSettings.thisUser')}{' '}
              {t('website.membersSettings.transferOwnershipDescription')}{' '}
              <span className="font-medium text-[#1A162F]">
                {t('website.membersSettings.adminRole')}
              </span>
              .
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={() => {
                setIsTransferOwnershipModalOpen(false);
                setTransferTargetMemberId('');
                setTransferTargetSnapshot(null);
              }}
              disabled={isTransferringOwnership}
              className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={() => {
                void handleTransferOwnership();
              }}
              disabled={!canConfirmOwnershipTransfer || isTransferringOwnership}
              className="flex items-center justify-center gap-2 rounded-[10px] bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(220,38,38,0.12)] transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isTransferringOwnership ? (
                <>
                  <CircleNotch className="w-4 h-4 animate-spin" />
                  <span>{t('website.membersSettings.transferring')}</span>
                </>
              ) : (
                t('website.membersSettings.transferOwnership')
              )}
            </button>
          </div>
        </div>
      </Modal>

      <MemberInviteUpgradeModal
        isOpen={adminProductFeatures.memberInvites && isInviteUpgradeModalOpen}
        onClose={() => setIsInviteUpgradeModalOpen(false)}
        onPrimaryAction={handleOpenInviteUpgradePricing}
        prompt={inviteUpgradePrompt}
      />

      <MemberInviteUpgradeModal
        isOpen={
          adminProductFeatures.rolesAndPermissions && isRolesUpgradeModalOpen
        }
        onClose={() => setIsRolesUpgradeModalOpen(false)}
        onPrimaryAction={handleOpenRolesUpgradePricing}
        prompt={rolesUpgradePrompt}
      />

      <MemberInviteLimitModal
        isOpen={adminProductFeatures.memberInvites && isInviteLimitModalOpen}
        onClose={() => setIsInviteLimitModalOpen(false)}
        onPrimaryAction={handleInviteLimitPrimaryAction}
        prompt={inviteLimitPrompt}
      />

      <ContactSalesModal
        isOpen={
          adminProductFeatures.memberInvites && isInviteLimitContactSalesOpen
        }
        onClose={() => setIsInviteLimitContactSalesOpen(false)}
      />

      {adminProductFeatures.rolesAndPermissions &&
      inlineEditingRole !== undefined &&
      canManageRoleAccess ? (
        <RoleDrawer
          role={inlineEditingRole}
          existingRoles={roles}
          assignedMemberCount={0}
          isPreparing={isInlineRolePreparing}
          onClose={closeInlineRoleDrawer}
          onSave={async (newRole) => {
            const savedRole = await handleSaveDataRole(newRole);
            setRoles((prev) => {
              const exists = prev.find((r) => r.id === savedRole.id);
              return exists
                ? prev.map((r) => (r.id === savedRole.id ? savedRole : r))
                : [...prev, savedRole];
            });
            return savedRole;
          }}
        />
      ) : null}
    </div>
  );
};

export default MembersSettings;
